import { quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import { createVersionedSessionDraftStore } from "@/app/booking/core/versionedSessionDraftStore";
import {
  SPECIALIST_LOCATION_GATEWAY_DEFAULT_TTL_MS,
  createSpecialistLocationGatewayNavigationOwnerKey,
  createSpecialistLocationGatewayPreDraftGcClaim,
  createSpecialistLocationGatewayPreDraftResolution,
  createSpecialistLocationGatewayIntentRecord,
  isSpecialistLocationGatewayFingerprint,
  isSpecialistLocationGatewayIdentifier,
  isSpecialistLocationGatewayRecordFresh,
  validateSpecialistLocationGatewayNavigationAuthority,
  validateSpecialistLocationGatewayIntentRecord,
  type SpecialistLocationGatewayCommittedNavigationLineageV1,
  type SpecialistLocationGatewayCommittedAuthRebindClaimV1,
  type SpecialistLocationGatewayAuthRebindClaimV1,
  type SpecialistLocationGatewayCatalogTransitionClaimV1,
  type SpecialistLocationGatewayDestinationLifecycleClaimV1,
  type SpecialistLocationGatewayDestinationKind,
  type SpecialistLocationGatewayEligibilityRowV1,
  type SpecialistLocationGatewayIntentRecordV1,
  type SpecialistLocationGatewayNavigationAuthorityV1,
  type SpecialistLocationGatewayNavigationCommitClaimV1,
  type SpecialistLocationGatewayPreDraftGcClaimV1,
  type SpecialistLocationGatewayPreDraftResolutionV1,
  type SpecialistLocationGatewayPublicationV1,
} from "./contract";
import {
  compareAndSwapSpecialistLocationGatewayIntentStrict,
  inspectSpecialistLocationGatewayIntentsStrict,
  inspectSpecialistLocationGatewayIntentExactPersisted,
  readSpecialistLocationGatewayIntentStrict,
  removeSpecialistLocationGatewayIntentStrict,
  restoreSpecialistLocationGatewayIntentStrict,
} from "./intentStore";
import {
  resolveSpecialistLocationGateway,
  type SpecialistLocationGatewayProviderV1,
} from "./provider";
import {
  beginSpecialistLocationGatewayCustomerOwnedWrite,
  completeSpecialistLocationGatewayCustomerOwnedWrite,
  specialistLocationGatewayCustomerOwnedWriteIsCurrent,
  specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent,
  type SpecialistLocationGatewayTerminalWriteLease,
} from "./terminalWriteCoordinator";

export const SPECIALIST_LOCATION_GATEWAY_AUTHORITY_STORAGE_KEY =
  "volera:specialist-location-gateway:navigation-authorities:v1";

const authorityStore =
  createVersionedSessionDraftStore<SpecialistLocationGatewayNavigationAuthorityV1>(
    {
      storageKey: SPECIALIST_LOCATION_GATEWAY_AUTHORITY_STORAGE_KEY,
      storeKind: "specialist-location-gateway-navigation-authorities",
      maxEntries: 16,
      maxAgeMs: 24 * 60 * 60 * 1_000,
      maxSerializedCharacters: 240_000,
      validate: validateSpecialistLocationGatewayNavigationAuthority,
    },
  );

function safeAuthorityEpoch(value: number | undefined) {
  try {
    const epoch = value ?? Date.now();
    return Number.isSafeInteger(epoch) && epoch >= 0 ? epoch : null;
  } catch {
    return null;
  }
}

function inspectAuthorityStrict(draftId: string) {
  try {
    return authorityStore.inspectPersistedDraft(draftId);
  } catch {
    return { status: "unreadable" } as const;
  }
}

function inspectAuthoritiesStrict(evaluatedAtEpochMs?: number) {
  try {
    return authorityStore.inspectPersistedDrafts(evaluatedAtEpochMs);
  } catch {
    return { status: "unreadable", drafts: [] } as const;
  }
}

function createAuthorityStrict(value: SpecialistLocationGatewayNavigationAuthorityV1) {
  try {
    return authorityStore.createDraftStrictPersisted(value);
  } catch {
    return { status: "unreadable" } as const;
  }
}

function replaceAuthorityStrict(
  expected: SpecialistLocationGatewayNavigationAuthorityV1,
  next: SpecialistLocationGatewayNavigationAuthorityV1,
) {
  try {
    return authorityStore.replaceDraftStrictPersisted(expected, next);
  } catch {
    return { status: "unreadable" } as const;
  }
}

function removeAuthorityStrict(
  expected: SpecialistLocationGatewayNavigationAuthorityV1,
) {
  try {
    return authorityStore.removeDraftStrictPersisted(expected);
  } catch {
    return { status: "unreadable" } as const;
  }
}

function ambiguous(status: string) {
  return (
    status === "unreadable" ||
    status === "persistence-failed" ||
    status === "readback-mismatch"
  );
}

/** Exactly one mutable resolution slot exists per intent pointer. */
export function storeSpecialistLocationGatewayPreDraftResolution(input: {
  intentRecord: SpecialistLocationGatewayIntentRecordV1;
  publication: SpecialistLocationGatewayPublicationV1;
  selectedRow: SpecialistLocationGatewayEligibilityRowV1;
  nowEpochMs?: number;
}) {
  const nowEpochMs = safeAuthorityEpoch(input.nowEpochMs);
  if (nowEpochMs === null) return { status: "invalid" } as const;
  let suppliedIntent: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    suppliedIntent = validateSpecialistLocationGatewayIntentRecord(
      input.intentRecord,
    );
  } catch {
    return { status: "invalid" } as const;
  }
  if (!suppliedIntent.valid) return { status: "invalid" } as const;
  const intentRecord = suppliedIntent.draft;
  const persistedIntent = readSpecialistLocationGatewayIntentStrict({
    handoffId: intentRecord.draftId,
    expectedCustomerAccountId: intentRecord.customerAccountId,
    expectedLocale: intentRecord.locale,
    expectedSourceLocationId: intentRecord.source.sourceLocationId,
    expectedSourceUserLocationId: intentRecord.source.sourceUserLocationId,
    nowEpochMs,
  });
  if (persistedIntent.status !== "ready") {
    return persistedIntent.reason === "unreadable"
      ? ({ status: "unreadable" } as const)
      : ({ status: "conflict", reason: "intent-drift" } as const);
  }
  try {
    if (JSON.stringify(persistedIntent.intent) !== JSON.stringify(intentRecord)) {
      return { status: "conflict", reason: "intent-drift" } as const;
    }
  } catch {
    return { status: "invalid" } as const;
  }
  let resolution: SpecialistLocationGatewayPreDraftResolutionV1 | null;
  try {
    resolution = createSpecialistLocationGatewayPreDraftResolution({
      resolutionId: intentRecord.draftId,
      intentRecord,
      publication: input.publication,
      selectedRow: input.selectedRow,
      nowEpochMs,
    });
  } catch {
    resolution = null;
  }
  if (!resolution) return { status: "invalid" } as const;

  const current = inspectAuthorityStrict(resolution.draftId);
  if (current.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" } as const;
  }
  if (current.status === "absent") {
    let created = createAuthorityStrict(resolution);
    if (created.status === "capacity") {
      const reclaimed =
        reclaimExpiredSpecialistLocationGatewayPreDraftCapacityStrict({
          nowEpochMs,
          excludingDraftId: resolution.draftId,
        });
      if (reclaimed.status === "recovery-required") {
        return reclaimed;
      }
      if (reclaimed.status === "reclaimed") {
        created = createAuthorityStrict(resolution);
      }
    }
    if (ambiguous(created.status)) quarantineCustomerOwnedSessionStorage();
    return created.status === "created"
      ? { status: "stored", resolution } as const
      : created;
  }
  if (
    current.draft.kind !==
      "specialist-location-gateway-pre-draft-resolution" ||
    !resolutionMatchesIntentRecord(current.draft, persistedIntent.intent)
  ) {
    return { status: "conflict", reason: "intent-consumed" } as const;
  }
  if (JSON.stringify(current.draft) === JSON.stringify(resolution)) {
    return { status: "stored", resolution: current.draft } as const;
  }
  const replaced = replaceAuthorityStrict(
    current.draft,
    resolution,
  );
  if (ambiguous(replaced.status)) quarantineCustomerOwnedSessionStorage();
  return replaced.status === "replaced"
    ? { status: "stored", resolution } as const
    : replaced;
}

type SpecialistLocationGatewayCustomerOwnedMutation = {
  signal: AbortSignal;
  isCurrentOwner: () => boolean;
};

function customerOwnedMutationIsCurrent(
  lease: SpecialistLocationGatewayTerminalWriteLease,
  input: SpecialistLocationGatewayCustomerOwnedMutation,
) {
  return (
    !input.signal.aborted &&
    specialistLocationGatewayCustomerOwnedWriteIsCurrent(
      lease,
      input.isCurrentOwner,
    ) &&
    !input.signal.aborted
  );
}

function exactAuthority(
  left: SpecialistLocationGatewayNavigationAuthorityV1,
  right: SpecialistLocationGatewayNavigationAuthorityV1,
) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function compensatePreDraftResolutionWrite(input: {
  lease: SpecialistLocationGatewayTerminalWriteLease;
  next: SpecialistLocationGatewayPreDraftResolutionV1;
  prior: SpecialistLocationGatewayNavigationAuthorityV1 | null;
}) {
  const preservePrior =
    specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease);
  const inspected = inspectAuthorityStrict(input.next.draftId);
  if (inspected.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return false;
  }

  const removeExact = (
    authority: SpecialistLocationGatewayNavigationAuthorityV1,
  ) => {
    const removed = removeAuthorityStrict(authority);
    return removed.status === "removed" || removed.status === "absent";
  };
  const restorePrior = () => {
    if (!input.prior) return true;
    const restored = createAuthorityStrict(input.prior);
    return restored.status === "created";
  };

  let compensated = false;
  if (inspected.status === "absent") {
    compensated = preservePrior ? restorePrior() : true;
  } else if (input.prior && exactAuthority(inspected.draft, input.prior)) {
    compensated = true;
  } else if (exactAuthority(inspected.draft, input.next)) {
    if (input.prior && preservePrior) {
      compensated =
        replaceAuthorityStrict(inspected.draft, input.prior).status ===
        "replaced";
    } else {
      compensated = removeExact(inspected.draft);
    }
  }

  if (!compensated) {
    quarantineCustomerOwnedSessionStorage();
    return false;
  }

  // Auth teardown may have started inside the compensation write. Never
  // resurrect the prior customer after its shared epoch has advanced.
  if (
    input.prior &&
    preservePrior &&
    !specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)
  ) {
    const after = inspectAuthorityStrict(input.prior.draftId);
    if (
      after.status === "present" &&
      exactAuthority(after.draft, input.prior) &&
      removeExact(after.draft)
    ) {
      return true;
    }
    if (after.status === "absent") return true;
    quarantineCustomerOwnedSessionStorage();
    return false;
  }
  return true;
}

/**
 * Mounted Location selection transaction. The synchronous auth-teardown
 * lease prevents a re-entrant owner from adopting this write, and any route
 * drift after the strict store is compensated back to the exact prior slot.
 */
export function storeSpecialistLocationGatewayPreDraftResolutionForOwnerStrict(
  input: Parameters<
    typeof storeSpecialistLocationGatewayPreDraftResolution
  >[0] &
    SpecialistLocationGatewayCustomerOwnedMutation,
) {
  let intentValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    intentValidation = validateSpecialistLocationGatewayIntentRecord(
      input.intentRecord,
    );
  } catch {
    return { status: "invalid" } as const;
  }
  if (!intentValidation.valid) return { status: "invalid" } as const;
  const ownerKey = JSON.stringify([
    "specialist-location-gateway-pre-draft-resolution-write-v1",
    intentValidation.draft.draftId,
    intentValidation.draft.ownerKey,
  ]);
  const lease = beginSpecialistLocationGatewayCustomerOwnedWrite(
    ownerKey,
    input.isCurrentOwner,
  );
  if (!lease || input.signal.aborted) {
    return { status: "conflict", reason: "owner-drift" } as const;
  }
  try {
    const priorInspection = inspectAuthorityStrict(
      intentValidation.draft.draftId,
    );
    if (priorInspection.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "unreadable" } as const;
    }
    if (!customerOwnedMutationIsCurrent(lease, input)) {
      return { status: "conflict", reason: "owner-drift" } as const;
    }
    const prior =
      priorInspection.status === "present" ? priorInspection.draft : null;
    const stored = storeSpecialistLocationGatewayPreDraftResolution(input);
    if (stored.status !== "stored") return stored;

    const mutated = !prior || !exactAuthority(prior, stored.resolution);
    const intentAfter = inspectSpecialistLocationGatewayIntentExactPersisted(
      intentValidation.draft,
    );
    const authorityAfter = inspectAuthorityStrict(stored.resolution.draftId);
    const finalExact =
      intentAfter.status === "present-exact" &&
      authorityAfter.status === "present" &&
      exactAuthority(authorityAfter.draft, stored.resolution);
    if (
      finalExact &&
      customerOwnedMutationIsCurrent(lease, input)
    ) {
      return stored;
    }

    if (
      mutated &&
      !compensatePreDraftResolutionWrite({
        lease,
        next: stored.resolution,
        prior,
      })
    ) {
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      } as const;
    }
    if (
      intentAfter.status === "unreadable" ||
      authorityAfter.status === "unreadable"
    ) {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      } as const;
    }
    return {
      status: "conflict",
      reason: customerOwnedMutationIsCurrent(lease, input)
        ? "intent-drift"
        : "owner-drift",
    } as const;
  } finally {
    completeSpecialistLocationGatewayCustomerOwnedWrite(lease);
  }
}

export type ReadSpecialistLocationGatewayResolutionResult =
  | {
      status: "ready";
      resolution: SpecialistLocationGatewayPreDraftResolutionV1;
    }
  | {
      status: "unavailable";
      reason:
        | "customer-owner-mismatch"
        | "expired"
        | "intent-consumed"
        | "locale-mismatch"
        | "missing"
        | "source-route-mismatch"
        | "unreadable";
    };

export function readSpecialistLocationGatewayResolutionStrict(input: {
  resolutionId: string;
  expectedCustomerAccountId: string | null;
  expectedLocale: string;
  expectedSourceLocationId: string;
  expectedSourceUserLocationId: string;
  nowEpochMs?: number;
}): ReadSpecialistLocationGatewayResolutionResult {
  const inspected = inspectAuthorityStrict(input.resolutionId);
  if (inspected.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unavailable", reason: "unreadable" };
  }
  if (inspected.status === "absent") {
    return { status: "unavailable", reason: "missing" };
  }
  if (
    inspected.draft.kind !==
    "specialist-location-gateway-pre-draft-resolution"
  ) {
    return { status: "unavailable", reason: "intent-consumed" };
  }
  const resolution = inspected.draft;
  const nowEpochMs = safeAuthorityEpoch(input.nowEpochMs);
  if (
    nowEpochMs === null ||
    !isSpecialistLocationGatewayRecordFresh(resolution, nowEpochMs)
  ) {
    return { status: "unavailable", reason: "expired" };
  }
  if (resolution.customerAccountId !== input.expectedCustomerAccountId) {
    return { status: "unavailable", reason: "customer-owner-mismatch" };
  }
  if (resolution.locale !== input.expectedLocale) {
    return { status: "unavailable", reason: "locale-mismatch" };
  }
  if (
    resolution.source.sourceLocationId !== input.expectedSourceLocationId ||
    resolution.source.sourceUserLocationId !==
      input.expectedSourceUserLocationId
  ) {
    return { status: "unavailable", reason: "source-route-mismatch" };
  }
  return { status: "ready", resolution };
}

export type ReadSpecialistLocationGatewaySelectedDestinationResult =
  | {
      status: "ready";
      resolution: SpecialistLocationGatewayPreDraftResolutionV1;
    }
  | {
      status: "unavailable";
      reason:
        | "customer-owner-mismatch"
        | "destination-mismatch"
        | "expired"
        | "intent-consumed"
        | "locale-mismatch"
        | "missing"
        | "unreadable";
    };

/**
 * Destination-B entry lookup. The Services URL does not repeat source A;
 * validated immutable A is returned only from the persisted resolution.
 */
export function readSpecialistLocationGatewayResolutionForSelectedDestinationStrict(
  input: {
    resolutionId: string;
    expectedCustomerAccountId: string | null;
    expectedLocale: string;
    expectedSelectedLocationId: string;
    expectedSelectedUserLocationId: string;
    nowEpochMs?: number;
  },
): ReadSpecialistLocationGatewaySelectedDestinationResult {
  if (
    !isSpecialistLocationGatewayIdentifier(input.resolutionId) ||
    !isSpecialistLocationGatewayIdentifier(input.expectedSelectedLocationId) ||
    !isSpecialistLocationGatewayIdentifier(
      input.expectedSelectedUserLocationId,
    )
  ) {
    return { status: "unavailable", reason: "unreadable" };
  }
  const inspected = inspectAuthorityStrict(input.resolutionId);
  if (inspected.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unavailable", reason: "unreadable" };
  }
  if (inspected.status === "absent") {
    return { status: "unavailable", reason: "missing" };
  }
  if (
    inspected.draft.kind !==
    "specialist-location-gateway-pre-draft-resolution"
  ) {
    return { status: "unavailable", reason: "intent-consumed" };
  }
  const resolution = inspected.draft;
  const nowEpochMs = safeAuthorityEpoch(input.nowEpochMs);
  if (
    nowEpochMs === null ||
    !isSpecialistLocationGatewayRecordFresh(resolution, nowEpochMs)
  ) {
    return { status: "unavailable", reason: "expired" };
  }
  if (resolution.customerAccountId !== input.expectedCustomerAccountId) {
    return { status: "unavailable", reason: "customer-owner-mismatch" };
  }
  if (resolution.locale !== input.expectedLocale) {
    return { status: "unavailable", reason: "locale-mismatch" };
  }
  if (
    resolution.selection.selectedLocationId !==
      input.expectedSelectedLocationId ||
    resolution.selection.selectedUserLocationId !==
      input.expectedSelectedUserLocationId
  ) {
    return { status: "unavailable", reason: "destination-mismatch" };
  }
  return { status: "ready", resolution };
}

export type DiscardSpecialistLocationGatewayPreDraftResult =
  | { status: "discarded" }
  | {
      status: "failed";
      reason:
        | "intent-consumed"
        | "invalid-input"
        | "missing"
        | "owner-drift"
        | "transition-in-progress";
    }
  | { status: "recovery-required"; reason: "ambiguous-persistence" };

/**
 * Exact Close teardown for Location, Options, and selected-B Services. A
 * catalog transition claim is intentionally non-discardable until its saga
 * restores or publishes one stable resolution. Authority is removed before
 * intent; any partial/ambiguous second write quarantines the whole lane.
 */
export function discardSpecialistLocationGatewayPreDraftStrict(input: {
  surface: "location" | "options" | "services";
  intent: SpecialistLocationGatewayIntentRecordV1;
  nowEpochMs?: number;
}): DiscardSpecialistLocationGatewayPreDraftResult {
  let intentValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    intentValidation = validateSpecialistLocationGatewayIntentRecord(
      input.intent,
    );
  } catch {
    return { status: "failed", reason: "invalid-input" };
  }
  const nowEpochMs = safeAuthorityEpoch(input.nowEpochMs);
  if (
    !intentValidation.valid ||
    nowEpochMs === null ||
    (input.surface !== "location" &&
      input.surface !== "options" &&
      input.surface !== "services")
  ) {
    return { status: "failed", reason: "invalid-input" };
  }
  const intent = intentValidation.draft;
  const authority = inspectAuthorityStrict(intent.draftId);
  if (authority.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (
    authority.status === "present" &&
    (authority.draft.kind ===
      "specialist-location-gateway-catalog-transition-claim" ||
      authority.draft.kind ===
        "specialist-location-gateway-auth-rebind-claim")
  ) {
    return resolutionMatchesIntentRecord(authority.draft, intent)
      ? { status: "failed", reason: "transition-in-progress" }
      : { status: "failed", reason: "owner-drift" };
  }
  const persistedIntent =
    inspectSpecialistLocationGatewayIntentExactPersisted(intent);
  if (persistedIntent.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (persistedIntent.status === "absent") {
    return { status: "failed", reason: "missing" };
  }
  if (persistedIntent.status !== "present-exact") {
    return { status: "failed", reason: "owner-drift" };
  }
  if (authority.status === "present") {
    if (
      authority.draft.kind !==
      "specialist-location-gateway-pre-draft-resolution"
    ) {
      return { status: "failed", reason: "intent-consumed" };
    }
    if (!resolutionMatchesIntentRecord(authority.draft, intent)) {
      return { status: "failed", reason: "owner-drift" };
    }
    const removedAuthority = removeAuthorityStrict(authority.draft);
    if (removedAuthority.status !== "removed") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
  }
  const removedIntent = removeSpecialistLocationGatewayIntentStrict(intent);
  if (removedIntent.status === "removed") {
    return { status: "discarded" };
  }
  if (authority.status === "present") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (removedIntent.status === "recovery-required") {
    return removedIntent;
  }
  return {
    status: "failed",
    reason: removedIntent.status === "absent" ? "missing" : "owner-drift",
  };
}

function restoreDiscardedPreDraftExact(input: {
  intent: SpecialistLocationGatewayIntentRecordV1;
  authority: SpecialistLocationGatewayPreDraftResolutionV1 | null;
  lease: SpecialistLocationGatewayTerminalWriteLease;
}) {
  const preservePrior =
    specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease);
  if (!preservePrior) return true;

  const intentInspection =
    inspectSpecialistLocationGatewayIntentExactPersisted(input.intent);
  if (intentInspection.status === "unreadable") return false;
  if (intentInspection.status === "absent") {
    const restoredIntent =
      restoreSpecialistLocationGatewayIntentStrict(input.intent);
    if (restoredIntent.status !== "restored") return false;
  } else if (intentInspection.status !== "present-exact") {
    return false;
  }

  if (input.authority) {
    const authorityInspection = inspectAuthorityStrict(input.authority.draftId);
    if (authorityInspection.status === "unreadable") return false;
    if (authorityInspection.status === "absent") {
      const restoredAuthority = createAuthorityStrict(input.authority);
      if (restoredAuthority.status !== "created") return false;
    } else if (!exactAuthority(authorityInspection.draft, input.authority)) {
      return false;
    }
  }

  // If auth teardown advanced while restoring, remove only our exact prior
  // values again so no old-customer state survives the completed barrier.
  if (
    !specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)
  ) {
    if (input.authority) {
      const authorityAfter = inspectAuthorityStrict(input.authority.draftId);
      if (
        authorityAfter.status === "present" &&
        exactAuthority(authorityAfter.draft, input.authority)
      ) {
        const removedAuthority = removeAuthorityStrict(authorityAfter.draft);
        if (
          removedAuthority.status !== "removed" &&
          removedAuthority.status !== "absent"
        ) {
          return false;
        }
      } else if (authorityAfter.status !== "absent") {
        return false;
      }
    }
    const intentAfter =
      inspectSpecialistLocationGatewayIntentExactPersisted(input.intent);
    if (intentAfter.status === "present-exact") {
      const removedIntent = removeSpecialistLocationGatewayIntentStrict(
        input.intent,
      );
      return (
        removedIntent.status === "removed" ||
        removedIntent.status === "absent"
      );
    }
    return intentAfter.status === "absent";
  }
  return true;
}

/**
 * Owner-aware pre-draft Close. Route drift restores the exact intent and
 * resolution removed by this operation; auth teardown epoch drift leaves the
 * stores empty and can never resurrect the prior customer.
 */
export function discardSpecialistLocationGatewayPreDraftForOwnerStrict(
  input: Parameters<typeof discardSpecialistLocationGatewayPreDraftStrict>[0] &
    SpecialistLocationGatewayCustomerOwnedMutation,
): DiscardSpecialistLocationGatewayPreDraftResult {
  let intentValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    intentValidation = validateSpecialistLocationGatewayIntentRecord(
      input.intent,
    );
  } catch {
    return { status: "failed", reason: "invalid-input" };
  }
  if (!intentValidation.valid) {
    return { status: "failed", reason: "invalid-input" };
  }
  const intent = intentValidation.draft;
  const lease = beginSpecialistLocationGatewayCustomerOwnedWrite(
    JSON.stringify([
      "specialist-location-gateway-pre-draft-discard-v1",
      intent.draftId,
      intent.ownerKey,
      input.surface,
    ]),
    input.isCurrentOwner,
  );
  if (!lease || input.signal.aborted) {
    return { status: "failed", reason: "owner-drift" };
  }
  try {
    const authorityBefore = inspectAuthorityStrict(intent.draftId);
    if (authorityBefore.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    const priorAuthority =
      authorityBefore.status === "present" &&
      authorityBefore.draft.kind ===
        "specialist-location-gateway-pre-draft-resolution"
        ? authorityBefore.draft
        : null;
    if (!customerOwnedMutationIsCurrent(lease, input)) {
      return { status: "failed", reason: "owner-drift" };
    }
    const discarded = discardSpecialistLocationGatewayPreDraftStrict(input);
    if (discarded.status !== "discarded") return discarded;

    const intentAfter = inspectSpecialistLocationGatewayIntentExactPersisted(
      intent,
    );
    const authorityAfter = inspectAuthorityStrict(intent.draftId);
    const current = customerOwnedMutationIsCurrent(lease, input);
    if (
      current &&
      intentAfter.status === "absent" &&
      authorityAfter.status === "absent"
    ) {
      return discarded;
    }

    if (
      specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease) &&
      !restoreDiscardedPreDraftExact({
        intent,
        authority: priorAuthority,
        lease,
      })
    ) {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    if (
      intentAfter.status === "unreadable" ||
      authorityAfter.status === "unreadable"
    ) {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    return { status: "failed", reason: "owner-drift" };
  } finally {
    completeSpecialistLocationGatewayCustomerOwnedWrite(lease);
  }
}

export type ResolveSpecialistLocationGatewaySelectedDestinationResult =
  | {
      status: "ready";
      intent: SpecialistLocationGatewayIntentRecordV1;
      resolution: SpecialistLocationGatewayPreDraftResolutionV1;
      publication: SpecialistLocationGatewayPublicationV1;
    }
  | {
      status: "unavailable";
      reason:
        | "aborted"
        | "intent-unavailable"
        | "owner-drift"
        | "provider-unavailable"
        | "resolution-unavailable"
        | "selection-drift";
    };

/** Re-reads intent + provider truth before selected-B Services becomes ready. */
export async function resolveSpecialistLocationGatewaySelectedDestinationStrict(
  input: {
    resolutionId: string;
    expectedCustomerAccountId: string | null;
    expectedLocale: string;
    expectedSelectedLocationId: string;
    expectedSelectedUserLocationId: string;
    provider: SpecialistLocationGatewayProviderV1;
    signal: AbortSignal;
    now: () => number;
    isCurrentOwner: () => boolean;
  },
): Promise<ResolveSpecialistLocationGatewaySelectedDestinationResult> {
  let nowEpochMs: number;
  try {
    nowEpochMs = input.now();
  } catch {
    return { status: "unavailable", reason: "resolution-unavailable" };
  }
  const selected =
    readSpecialistLocationGatewayResolutionForSelectedDestinationStrict({
      ...input,
      nowEpochMs,
    });
  if (selected.status !== "ready") {
    return { status: "unavailable", reason: "resolution-unavailable" };
  }
  const resolution = selected.resolution;
  const pairedIntent = readSpecialistLocationGatewayIntentStrict({
    handoffId: resolution.intentDraftId,
    nowEpochMs,
    expectedCustomerAccountId: input.expectedCustomerAccountId,
    expectedLocale: input.expectedLocale,
    expectedSourceLocationId: resolution.source.sourceLocationId,
    expectedSourceUserLocationId: resolution.source.sourceUserLocationId,
  });
  if (
    pairedIntent.status !== "ready" ||
    !resolutionMatchesIntentRecord(resolution, pairedIntent.intent)
  ) {
    return { status: "unavailable", reason: "intent-unavailable" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return {
      status: "unavailable",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const resolved = await resolveSpecialistLocationGateway({
    intent: pairedIntent.intent,
    provider: input.provider,
    signal: input.signal,
    now: input.now,
    isCurrentHandoff: (candidate) =>
      candidate.ownerKey === pairedIntent.intent.ownerKey &&
      operationIsCurrent(input.signal, input.isCurrentOwner),
  });
  if (resolved.status !== "ready") {
    return {
      status: "unavailable",
      reason: input.signal.aborted ? "aborted" : "provider-unavailable",
    };
  }
  let completedAtEpochMs: number;
  try {
    completedAtEpochMs = input.now();
  } catch {
    return { status: "unavailable", reason: "provider-unavailable" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return {
      status: "unavailable",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const selectedAfter =
    readSpecialistLocationGatewayResolutionForSelectedDestinationStrict({
      ...input,
      nowEpochMs: completedAtEpochMs,
    });
  const intentAfter = readSpecialistLocationGatewayIntentStrict({
    handoffId: resolution.intentDraftId,
    nowEpochMs: completedAtEpochMs,
    expectedCustomerAccountId: input.expectedCustomerAccountId,
    expectedLocale: input.expectedLocale,
    expectedSourceLocationId: resolution.source.sourceLocationId,
    expectedSourceUserLocationId: resolution.source.sourceUserLocationId,
  });
  if (
    selectedAfter.status !== "ready" ||
    intentAfter.status !== "ready" ||
    JSON.stringify(selectedAfter.resolution) !== JSON.stringify(resolution) ||
    JSON.stringify(intentAfter.intent) !== JSON.stringify(pairedIntent.intent)
  ) {
    return { status: "unavailable", reason: "owner-drift" };
  }
  const revalidated = revalidateSpecialistLocationGatewayResolution({
    resolution,
    intentRecord: intentAfter.intent,
    publication: resolved.publication,
    nowEpochMs: completedAtEpochMs,
  });
  if (!revalidated || !operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return {
      status: "unavailable",
      reason: input.signal.aborted ? "aborted" : "selection-drift",
    };
  }
  return {
    status: "ready",
    intent: intentAfter.intent,
    resolution: revalidated,
    publication: resolved.publication,
  };
}

export function revalidateSpecialistLocationGatewayResolution(input: {
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
  intentRecord: SpecialistLocationGatewayIntentRecordV1;
  publication: SpecialistLocationGatewayPublicationV1;
  nowEpochMs: number;
}) {
  const current = validateSpecialistLocationGatewayNavigationAuthority(
    input.resolution,
  );
  if (
    !current.valid ||
    current.draft.kind !==
      "specialist-location-gateway-pre-draft-resolution" ||
    !isSpecialistLocationGatewayRecordFresh(current.draft, input.nowEpochMs)
  ) {
    return null;
  }
  const selectedRows = input.publication.rows.filter(
    (row) =>
      row.locationId === input.resolution.selection.selectedLocationId &&
      row.userLocationId === input.resolution.selection.selectedUserLocationId,
  );
  if (selectedRows.length !== 1) return null;
  const rebuilt = createSpecialistLocationGatewayPreDraftResolution({
    resolutionId: input.resolution.draftId,
    intentRecord: input.intentRecord,
    publication: input.publication,
    selectedRow: selectedRows[0]!,
    nowEpochMs: input.nowEpochMs,
  });
  if (!rebuilt) return null;
  const stableTruth = (
    value: SpecialistLocationGatewayPreDraftResolutionV1,
  ) => ({
    schemaVersion: value.schemaVersion,
    kind: value.kind,
    mutationRevision: value.mutationRevision,
    draftId: value.draftId,
    gatewayProviderId: value.gatewayProviderId,
    gatewayProviderRevision: value.gatewayProviderRevision,
    customerAccountId: value.customerAccountId,
    locale: value.locale,
    generation: value.generation,
    intentDraftId: value.intentDraftId,
    sourceAction: value.sourceAction,
    source: value.source,
    intent: value.intent,
    selection: value.selection,
  });
  return JSON.stringify(stableTruth(rebuilt)) ===
    JSON.stringify(stableTruth(current.draft))
    ? current.draft
    : null;
}

function createAuthRebindClaim(input: {
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
  authenticatedIntent: SpecialistLocationGatewayIntentRecordV1;
  attemptId: string;
  claimedAtEpochMs: number;
}) {
  const { resolution, authenticatedIntent } = input;
  if (
    resolution.customerAccountId !== null ||
    authenticatedIntent.customerAccountId === null ||
    !isSpecialistLocationGatewayIdentifier(input.attemptId) ||
    !isSpecialistLocationGatewayRecordFresh(
      resolution,
      input.claimedAtEpochMs,
    ) ||
    authenticatedIntent.draftId !== resolution.intentDraftId ||
    authenticatedIntent.gatewayProviderId !== resolution.gatewayProviderId ||
    authenticatedIntent.gatewayProviderRevision !==
      resolution.gatewayProviderRevision ||
    authenticatedIntent.locale !== resolution.locale ||
    authenticatedIntent.generation !== resolution.generation + 1 ||
    authenticatedIntent.createdAtEpochMs > input.claimedAtEpochMs ||
    authenticatedIntent.expiresAtEpochMs > resolution.expiresAtEpochMs ||
    authenticatedIntent.sourceAction !== resolution.sourceAction ||
    JSON.stringify(authenticatedIntent.source) !==
      JSON.stringify(resolution.source) ||
    JSON.stringify(authenticatedIntent.intent) !==
      JSON.stringify(resolution.intent)
  ) {
    return null;
  }
  const body = navigationBase(resolution);
  const claim: SpecialistLocationGatewayAuthRebindClaimV1 = {
    ...body,
    kind: "specialist-location-gateway-auth-rebind-claim",
    mutationRevision: 4,
    attemptId: input.attemptId,
    authenticatedCustomerAccountId:
      authenticatedIntent.customerAccountId,
    targetGeneration: authenticatedIntent.generation,
    targetCreatedAtEpochMs: authenticatedIntent.createdAtEpochMs,
    targetExpiresAtEpochMs: authenticatedIntent.expiresAtEpochMs,
    claimedAtEpochMs: input.claimedAtEpochMs,
    ownerKey: createSpecialistLocationGatewayNavigationOwnerKey({
      phase: "auth-rebind",
      ...body,
      attemptId: input.attemptId,
      authenticatedCustomerAccountId:
        authenticatedIntent.customerAccountId,
      targetGeneration: authenticatedIntent.generation,
      targetCreatedAtEpochMs: authenticatedIntent.createdAtEpochMs,
      targetExpiresAtEpochMs: authenticatedIntent.expiresAtEpochMs,
      claimedAtEpochMs: input.claimedAtEpochMs,
    }),
  };
  const validation = validateSpecialistLocationGatewayNavigationAuthority(
    claim,
  );
  return validation.valid &&
    validation.draft.kind ===
      "specialist-location-gateway-auth-rebind-claim"
    ? validation.draft
    : null;
}

function removeAuthRebindOwnedBytesAfterEpoch(input: {
  intentCandidates: readonly SpecialistLocationGatewayIntentRecordV1[];
  authorityCandidates: readonly SpecialistLocationGatewayNavigationAuthorityV1[];
}) {
  let intentIsSafe = false;
  for (const candidate of input.intentCandidates) {
    const inspected = inspectSpecialistLocationGatewayIntentExactPersisted(
      candidate,
    );
    if (inspected.status === "unreadable") return false;
    if (inspected.status === "absent") {
      intentIsSafe = true;
      break;
    }
    if (inspected.status === "present-exact") {
      const removed = removeSpecialistLocationGatewayIntentStrict(candidate);
      if (removed.status === "removed" || removed.status === "absent") {
        intentIsSafe = true;
        break;
      }
      const after = inspectSpecialistLocationGatewayIntentExactPersisted(
        candidate,
      );
      if (after.status === "absent" || after.status === "conflict") {
        intentIsSafe = true;
        break;
      }
      return false;
    }
  }
  if (!intentIsSafe) {
    // Every exact candidate conflicted with a foreign current owner. It must
    // remain untouched.
    intentIsSafe = true;
  }

  const authorityId = input.authorityCandidates[0]?.draftId;
  if (!authorityId) return false;
  const inspectedAuthority = inspectAuthorityStrict(authorityId);
  if (inspectedAuthority.status === "unreadable") return false;
  if (inspectedAuthority.status === "absent") return intentIsSafe;
  const ownedAuthority = input.authorityCandidates.find((candidate) =>
    exactAuthority(inspectedAuthority.draft, candidate),
  );
  if (!ownedAuthority) return intentIsSafe;
  const removedAuthority = removeAuthorityStrict(ownedAuthority);
  if (
    removedAuthority.status === "removed" ||
    removedAuthority.status === "absent"
  ) {
    return intentIsSafe;
  }
  const authorityAfter = inspectAuthorityStrict(authorityId);
  return (
    intentIsSafe &&
    (authorityAfter.status === "absent" ||
      (authorityAfter.status === "present" &&
        !input.authorityCandidates.some((candidate) =>
          exactAuthority(authorityAfter.draft, candidate),
        )))
  );
}

function restoreAuthRebindAuthority(input: {
  lease: SpecialistLocationGatewayTerminalWriteLease;
  claim: SpecialistLocationGatewayAuthRebindClaimV1;
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
}) {
  if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
    return removeAuthRebindOwnedBytesAfterEpoch({
      intentCandidates: [],
      authorityCandidates: [input.claim, input.resolution],
    });
  }
  const restored = replaceAuthorityStrict(input.claim, input.resolution);
  if (restored.status === "replaced") {
    if (specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
      return true;
    }
    return removeAuthRebindOwnedBytesAfterEpoch({
      intentCandidates: [],
      authorityCandidates: [input.claim, input.resolution],
    });
  }
  if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
    return removeAuthRebindOwnedBytesAfterEpoch({
      intentCandidates: [],
      authorityCandidates: [input.claim, input.resolution],
    });
  }
  quarantineCustomerOwnedSessionStorage();
  return false;
}

function rollbackAuthRebind(input: {
  lease: SpecialistLocationGatewayTerminalWriteLease;
  claim: SpecialistLocationGatewayAuthRebindClaimV1;
  anonymousIntent: SpecialistLocationGatewayIntentRecordV1;
  anonymousResolution: SpecialistLocationGatewayPreDraftResolutionV1;
  authenticatedIntent: SpecialistLocationGatewayIntentRecordV1;
}) {
  const removeAfterEpoch = () =>
    removeAuthRebindOwnedBytesAfterEpoch({
      intentCandidates: [input.authenticatedIntent, input.anonymousIntent],
      authorityCandidates: [input.claim, input.anonymousResolution],
    });
  if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
    return removeAfterEpoch();
  }
  const intentRollback = compareAndSwapSpecialistLocationGatewayIntentStrict({
    expected: input.authenticatedIntent,
    next: input.anonymousIntent,
  });
  if (intentRollback.status !== "replaced") {
    if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
      return removeAfterEpoch();
    }
    quarantineCustomerOwnedSessionStorage();
    return false;
  }
  if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
    return removeAfterEpoch();
  }
  return restoreAuthRebindAuthority({
    lease: input.lease,
    claim: input.claim,
    resolution: input.anonymousResolution,
  });
}

export type BindSpecialistLocationGatewayPreDraftAfterAuthResult =
  | {
      status: "bound";
      intent: SpecialistLocationGatewayIntentRecordV1;
      resolution: SpecialistLocationGatewayPreDraftResolutionV1;
    }
  | {
      status: "failed";
      reason:
        | "aborted"
        | "already-owned"
        | "invalid-account"
        | "invalid-input"
        | "owner-drift"
        | "provider-unavailable"
        | "selection-drift";
    }
  | { status: "recovery-required"; reason: "ambiguous-persistence" };

/**
 * The only safe pre-draft anonymous -> authenticated transition. Provider
 * truth is resolved before mutation, then resolution -> claim serializes
 * against Continue/catalog switching while intent and authority are advanced.
 */
export async function bindSpecialistLocationGatewayPreDraftAfterAuthStrict(
  input: {
    anonymousIntent: SpecialistLocationGatewayIntentRecordV1;
    anonymousResolution: SpecialistLocationGatewayPreDraftResolutionV1;
    authenticatedCustomerAccountId: string;
    attemptId: string;
    provider: SpecialistLocationGatewayProviderV1;
    signal: AbortSignal;
    now: () => number;
    isCurrentOwner: () => boolean;
  },
): Promise<BindSpecialistLocationGatewayPreDraftAfterAuthResult> {
  let intentValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  let resolutionValidation: ReturnType<
    typeof validateSpecialistLocationGatewayNavigationAuthority
  >;
  try {
    intentValidation = validateSpecialistLocationGatewayIntentRecord(
      input.anonymousIntent,
    );
    resolutionValidation = validateSpecialistLocationGatewayNavigationAuthority(
      input.anonymousResolution,
    );
  } catch {
    return { status: "failed", reason: "invalid-input" };
  }
  if (
    !intentValidation.valid ||
    !resolutionValidation.valid ||
    resolutionValidation.draft.kind !==
      "specialist-location-gateway-pre-draft-resolution" ||
    !isSpecialistLocationGatewayIdentifier(
      input.authenticatedCustomerAccountId,
    ) ||
    !isSpecialistLocationGatewayIdentifier(input.attemptId)
  ) {
    return {
      status: "failed",
      reason: isSpecialistLocationGatewayIdentifier(
        input.authenticatedCustomerAccountId,
      )
        ? "invalid-input"
        : "invalid-account",
    };
  }
  const anonymousIntent = intentValidation.draft;
  const anonymousResolution = resolutionValidation.draft;
  if (
    anonymousIntent.customerAccountId !== null ||
    anonymousResolution.customerAccountId !== null
  ) {
    return { status: "failed", reason: "already-owned" };
  }
  if (
    anonymousIntent.generation >= Number.MAX_SAFE_INTEGER ||
    !resolutionMatchesIntentRecord(anonymousResolution, anonymousIntent) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }

  let startedAtEpochMs: number;
  try {
    startedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "provider-unavailable" };
  }
  if (
    safeAuthorityEpoch(startedAtEpochMs) === null ||
    !isSpecialistLocationGatewayRecordFresh(
      anonymousResolution,
      startedAtEpochMs,
    ) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  let authenticatedIntent: SpecialistLocationGatewayIntentRecordV1 | null =
    null;
  try {
    const candidate = createSpecialistLocationGatewayIntentRecord({
      draftId: anonymousIntent.draftId,
      gatewayProviderId: anonymousIntent.gatewayProviderId,
      gatewayProviderRevision: anonymousIntent.gatewayProviderRevision,
      customerAccountId: input.authenticatedCustomerAccountId,
      locale: anonymousIntent.locale,
      generation: anonymousIntent.generation + 1,
      createdAtEpochMs: startedAtEpochMs,
      expiresAtEpochMs: anonymousResolution.expiresAtEpochMs,
      sourceAction: anonymousIntent.sourceAction,
      source: anonymousIntent.source,
      intent: anonymousIntent.intent,
    });
    const validation = validateSpecialistLocationGatewayIntentRecord(candidate);
    authenticatedIntent = validation.valid ? validation.draft : null;
  } catch {
    authenticatedIntent = null;
  }
  if (!authenticatedIntent) {
    return { status: "failed", reason: "invalid-input" };
  }

  const persistedIntentBefore = readSpecialistLocationGatewayIntentStrict({
    handoffId: anonymousIntent.draftId,
    expectedCustomerAccountId: null,
    expectedLocale: anonymousIntent.locale,
    expectedSourceLocationId: anonymousIntent.source.sourceLocationId,
    expectedSourceUserLocationId:
      anonymousIntent.source.sourceUserLocationId,
    nowEpochMs: startedAtEpochMs,
  });
  const persistedAuthorityBefore = inspectAuthorityStrict(
    anonymousResolution.draftId,
  );
  if (
    persistedIntentBefore.status !== "ready" ||
    JSON.stringify(persistedIntentBefore.intent) !==
      JSON.stringify(anonymousIntent) ||
    persistedAuthorityBefore.status !== "present" ||
    JSON.stringify(persistedAuthorityBefore.draft) !==
      JSON.stringify(anonymousResolution)
  ) {
    if (persistedAuthorityBefore.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }

  const resolved = await resolveSpecialistLocationGateway({
    intent: authenticatedIntent,
    provider: input.provider,
    signal: input.signal,
    now: input.now,
    isCurrentHandoff: (candidate) =>
      candidate.ownerKey === authenticatedIntent?.ownerKey &&
      operationIsCurrent(input.signal, input.isCurrentOwner),
  });
  if (
    resolved.status !== "ready" ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "provider-unavailable",
    };
  }

  let claimedAtEpochMs: number;
  try {
    claimedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "provider-unavailable" };
  }
  if (
    safeAuthorityEpoch(claimedAtEpochMs) === null ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const selectedRows = resolved.publication.rows.filter(
    (row) =>
      row.locationId === anonymousResolution.selection.selectedLocationId &&
      row.userLocationId ===
        anonymousResolution.selection.selectedUserLocationId,
  );
  const authenticatedResolution =
    selectedRows.length === 1
      ? createSpecialistLocationGatewayPreDraftResolution({
          resolutionId: anonymousResolution.draftId,
          intentRecord: authenticatedIntent,
          publication: resolved.publication,
          selectedRow: selectedRows[0]!,
          nowEpochMs: claimedAtEpochMs,
        })
      : null;
  if (
    !authenticatedResolution ||
    JSON.stringify(authenticatedResolution.selection) !==
      JSON.stringify(anonymousResolution.selection)
  ) {
    return { status: "failed", reason: "selection-drift" };
  }
  const claim = createAuthRebindClaim({
    resolution: anonymousResolution,
    authenticatedIntent,
    attemptId: input.attemptId,
    claimedAtEpochMs,
  });
  if (!claim) return { status: "failed", reason: "invalid-input" };

  const mutation = {
    signal: input.signal,
    isCurrentOwner: input.isCurrentOwner,
  };
  const lease = beginSpecialistLocationGatewayCustomerOwnedWrite(
    JSON.stringify([
      "specialist-location-gateway-pre-draft-auth-bind-v1",
      anonymousIntent.ownerKey,
      input.authenticatedCustomerAccountId,
      input.attemptId,
    ]),
    input.isCurrentOwner,
  );
  if (!lease || input.signal.aborted) {
    if (lease) completeSpecialistLocationGatewayCustomerOwnedWrite(lease);
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const bindMutationIsCurrent = () =>
    customerOwnedMutationIsCurrent(lease, mutation);

  try {
  const persistedIntentAfter = readSpecialistLocationGatewayIntentStrict({
    handoffId: anonymousIntent.draftId,
    expectedCustomerAccountId: null,
    expectedLocale: anonymousIntent.locale,
    expectedSourceLocationId: anonymousIntent.source.sourceLocationId,
    expectedSourceUserLocationId:
      anonymousIntent.source.sourceUserLocationId,
    nowEpochMs: claimedAtEpochMs,
  });
  const persistedAuthorityAfter = inspectAuthorityStrict(
    anonymousResolution.draftId,
  );
  if (
    persistedIntentAfter.status !== "ready" ||
    JSON.stringify(persistedIntentAfter.intent) !==
      JSON.stringify(anonymousIntent) ||
    persistedAuthorityAfter.status !== "present" ||
    JSON.stringify(persistedAuthorityAfter.draft) !==
      JSON.stringify(anonymousResolution) ||
    !bindMutationIsCurrent()
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }

  const claimed = replaceAuthorityStrict(anonymousResolution, claim);
  if (
    claimed.status !== "replaced" ||
    claimed.draft.kind !==
      "specialist-location-gateway-auth-rebind-claim"
  ) {
    if (ambiguous(claimed.status)) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }
  const persistedClaim = claimed.draft;
  if (!bindMutationIsCurrent()) {
    if (
      !restoreAuthRebindAuthority({
        lease,
        claim: persistedClaim,
        resolution: anonymousResolution,
      })
    ) {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }

  const intentTransition =
    compareAndSwapSpecialistLocationGatewayIntentStrict({
      expected: anonymousIntent,
      next: authenticatedIntent,
    });
  if (intentTransition.status !== "replaced") {
    if (intentTransition.status === "recovery-required") {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    if (
      !restoreAuthRebindAuthority({
        lease,
        claim: persistedClaim,
        resolution: anonymousResolution,
      })
    ) {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }
  const persistedAuthenticatedIntent = intentTransition.intent;
  if (!bindMutationIsCurrent()) {
    if (
      !rollbackAuthRebind({
        lease,
        claim: persistedClaim,
        anonymousIntent,
        anonymousResolution,
        authenticatedIntent: persistedAuthenticatedIntent,
      })
    ) {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }

  const intentProof = readSpecialistLocationGatewayIntentStrict({
    handoffId: persistedAuthenticatedIntent.draftId,
    expectedCustomerAccountId:
      persistedAuthenticatedIntent.customerAccountId,
    expectedLocale: persistedAuthenticatedIntent.locale,
    expectedSourceLocationId:
      persistedAuthenticatedIntent.source.sourceLocationId,
    expectedSourceUserLocationId:
      persistedAuthenticatedIntent.source.sourceUserLocationId,
    nowEpochMs: claimedAtEpochMs,
  });
  const claimProof = inspectAuthorityStrict(persistedClaim.draftId);
  if (
    intentProof.status !== "ready" ||
    JSON.stringify(intentProof.intent) !==
      JSON.stringify(persistedAuthenticatedIntent) ||
    claimProof.status !== "present" ||
    JSON.stringify(claimProof.draft) !== JSON.stringify(persistedClaim)
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (!bindMutationIsCurrent()) {
    if (
      !rollbackAuthRebind({
        lease,
        claim: persistedClaim,
        anonymousIntent,
        anonymousResolution,
        authenticatedIntent: persistedAuthenticatedIntent,
      })
    ) {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const completed = replaceAuthorityStrict(
    persistedClaim,
    authenticatedResolution,
  );
  if (
    completed.status === "replaced" &&
    completed.draft.kind ===
      "specialist-location-gateway-pre-draft-resolution"
  ) {
    const rollbackCompleted = () => {
      const removeAfterEpoch = () =>
        removeAuthRebindOwnedBytesAfterEpoch({
          intentCandidates: [
            persistedAuthenticatedIntent,
            anonymousIntent,
          ],
          authorityCandidates: [
            completed.draft,
            persistedClaim,
            anonymousResolution,
          ],
        });
      if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease)) {
        return removeAfterEpoch();
      }
      const claimRestored = replaceAuthorityStrict(
        completed.draft,
        persistedClaim,
      );
      if (
        claimRestored.status !== "replaced" ||
        claimRestored.draft.kind !==
          "specialist-location-gateway-auth-rebind-claim"
      ) {
        if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease)) {
          return removeAfterEpoch();
        }
        quarantineCustomerOwnedSessionStorage();
        return false;
      }
      if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease)) {
        return removeAfterEpoch();
      }
      return rollbackAuthRebind({
        lease,
        claim: claimRestored.draft,
        anonymousIntent,
        anonymousResolution,
        authenticatedIntent: persistedAuthenticatedIntent,
      });
    };
    if (!bindMutationIsCurrent()) {
      if (!rollbackCompleted()) {
        return {
          status: "recovery-required",
          reason: "ambiguous-persistence",
        };
      }
      return {
        status: "failed",
        reason: input.signal.aborted ? "aborted" : "owner-drift",
      };
    }
    const finalIntentProof = readSpecialistLocationGatewayIntentStrict({
      handoffId: persistedAuthenticatedIntent.draftId,
      expectedCustomerAccountId:
        persistedAuthenticatedIntent.customerAccountId,
      expectedLocale: persistedAuthenticatedIntent.locale,
      expectedSourceLocationId:
        persistedAuthenticatedIntent.source.sourceLocationId,
      expectedSourceUserLocationId:
        persistedAuthenticatedIntent.source.sourceUserLocationId,
      nowEpochMs: claimedAtEpochMs,
    });
    const finalResolutionProof = inspectAuthorityStrict(completed.draft.draftId);
    const finalPairIsExact =
      finalIntentProof.status === "ready" &&
      JSON.stringify(finalIntentProof.intent) ===
        JSON.stringify(persistedAuthenticatedIntent) &&
      finalResolutionProof.status === "present" &&
      JSON.stringify(finalResolutionProof.draft) ===
        JSON.stringify(completed.draft);
    if (
      !finalPairIsExact ||
      !bindMutationIsCurrent()
    ) {
      if (finalPairIsExact && rollbackCompleted()) {
        return {
          status: "failed",
          reason: input.signal.aborted ? "aborted" : "owner-drift",
        };
      }
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "bound",
      intent: persistedAuthenticatedIntent,
      resolution: completed.draft,
    };
  }
  quarantineCustomerOwnedSessionStorage();
  return { status: "recovery-required", reason: "ambiguous-persistence" };
  } finally {
    completeSpecialistLocationGatewayCustomerOwnedWrite(lease);
  }
}

function destinationAuthorized(
  resolution: SpecialistLocationGatewayPreDraftResolutionV1,
  destinationKind: SpecialistLocationGatewayDestinationKind,
) {
  const authority = resolution.selection.authority;
  return destinationKind === "package"
    ? authority.kind === "package"
    : authority.kind === "appointment" &&
        (destinationKind === "group"
          ? authority.groupCapability
          : authority.individualCapability);
}

function navigationBase(
  authority:
    | SpecialistLocationGatewayCommittedAuthRebindClaimV1
    | SpecialistLocationGatewayCommittedNavigationLineageV1
    | SpecialistLocationGatewayDestinationLifecycleClaimV1
    | SpecialistLocationGatewayPreDraftResolutionV1
    | SpecialistLocationGatewayNavigationCommitClaimV1,
) {
  return {
    schemaVersion: authority.schemaVersion,
    draftId: authority.draftId,
    gatewayProviderId: authority.gatewayProviderId,
    gatewayProviderRevision: authority.gatewayProviderRevision,
    customerAccountId: authority.customerAccountId,
    locale: authority.locale,
    generation: authority.generation,
    intentDraftId: authority.intentDraftId,
    createdAtEpochMs: authority.createdAtEpochMs,
    expiresAtEpochMs: authority.expiresAtEpochMs,
    sourceAction: authority.sourceAction,
    source: authority.source,
    intent: authority.intent,
    selection: authority.selection,
  };
}

function createCommitClaim(input: {
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
  attemptId: string;
  destinationKind: SpecialistLocationGatewayDestinationKind;
  destinationDraftId: string;
  expectedDestinationFingerprint: string;
  claimedAtEpochMs: number;
}) {
  if (
    !isSpecialistLocationGatewayIdentifier(input.attemptId) ||
    !isSpecialistLocationGatewayIdentifier(input.destinationDraftId) ||
    !isSpecialistLocationGatewayFingerprint(
      input.expectedDestinationFingerprint,
    ) ||
    !isSpecialistLocationGatewayRecordFresh(
      input.resolution,
      input.claimedAtEpochMs,
    ) ||
    !destinationAuthorized(input.resolution, input.destinationKind)
  ) {
    return null;
  }
  const body = navigationBase(input.resolution);
  const claim: SpecialistLocationGatewayNavigationCommitClaimV1 = {
    ...body,
    kind: "specialist-location-gateway-navigation-commit-claim",
    mutationRevision: 1,
    attemptId: input.attemptId,
    destinationKind: input.destinationKind,
    destinationDraftId: input.destinationDraftId,
    destinationDraftFingerprint: input.expectedDestinationFingerprint,
    claimedAtEpochMs: input.claimedAtEpochMs,
    ownerKey: createSpecialistLocationGatewayNavigationOwnerKey({
      phase: "claim",
      ...body,
      attemptId: input.attemptId,
      destinationKind: input.destinationKind,
      destinationDraftId: input.destinationDraftId,
      destinationDraftFingerprint: input.expectedDestinationFingerprint,
      claimedAtEpochMs: input.claimedAtEpochMs,
    }),
  };
  const validation = validateSpecialistLocationGatewayNavigationAuthority(claim);
  return validation.valid &&
    validation.draft.kind ===
      "specialist-location-gateway-navigation-commit-claim"
    ? validation.draft
    : null;
}

function createCommittedLineage(input: {
  claim: SpecialistLocationGatewayNavigationCommitClaimV1;
  committedAtEpochMs: number;
}) {
  if (!isSpecialistLocationGatewayRecordFresh(input.claim, input.committedAtEpochMs)) {
    return null;
  }
  const body = {
    ...navigationBase(input.claim),
    attemptId: input.claim.attemptId,
    destinationKind: input.claim.destinationKind,
    destinationDraftId: input.claim.destinationDraftId,
    destinationDraftFingerprint: input.claim.destinationDraftFingerprint,
  };
  const lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 = {
    ...body,
    kind: "specialist-location-gateway-navigation-lineage",
    mutationRevision: 2,
    resolutionId: input.claim.draftId,
    committedAtEpochMs: input.committedAtEpochMs,
    ownerKey: createSpecialistLocationGatewayNavigationOwnerKey({
      phase: "committed",
      ...body,
      resolutionId: input.claim.draftId,
      committedAtEpochMs: input.committedAtEpochMs,
    }),
  };
  const validation = validateSpecialistLocationGatewayNavigationAuthority(
    lineage,
  );
  return validation.valid &&
    validation.draft.kind ===
      "specialist-location-gateway-navigation-lineage"
    ? validation.draft
    : null;
}

function resolutionMatchesIntentRecord(
  resolution:
    | SpecialistLocationGatewayAuthRebindClaimV1
    | SpecialistLocationGatewayCatalogTransitionClaimV1
    | SpecialistLocationGatewayCommittedAuthRebindClaimV1
    | SpecialistLocationGatewayCommittedNavigationLineageV1
    | SpecialistLocationGatewayDestinationLifecycleClaimV1
    | SpecialistLocationGatewayPreDraftResolutionV1,
  intent: SpecialistLocationGatewayIntentRecordV1,
) {
  try {
    return (
      resolution.draftId === intent.draftId &&
      resolution.intentDraftId === intent.draftId &&
      resolution.gatewayProviderId === intent.gatewayProviderId &&
      resolution.gatewayProviderRevision === intent.gatewayProviderRevision &&
      resolution.customerAccountId === intent.customerAccountId &&
      resolution.locale === intent.locale &&
      resolution.generation === intent.generation &&
      resolution.createdAtEpochMs >= intent.createdAtEpochMs &&
      resolution.expiresAtEpochMs <= intent.expiresAtEpochMs &&
      resolution.sourceAction === intent.sourceAction &&
      JSON.stringify(resolution.source) === JSON.stringify(intent.source) &&
      JSON.stringify(resolution.intent) === JSON.stringify(intent.intent)
    );
  } catch {
    return false;
  }
}

export type ReclaimExpiredSpecialistLocationGatewayPreDraftCapacityResult =
  | { status: "reclaimed"; reclaimedCount: number }
  | { status: "none" }
  | { status: "recovery-required"; reason: "ambiguous-persistence" };

function compensateSpecialistLocationGatewayPreDraftGcClaimExact(
  claim: SpecialistLocationGatewayPreDraftGcClaimV1,
) {
  const compensated = claim.priorResolution
    ? replaceAuthorityStrict(claim, claim.priorResolution)
    : removeAuthorityStrict(claim);
  if (
    (claim.priorResolution && compensated.status === "replaced") ||
    (!claim.priorResolution && compensated.status === "removed")
  ) {
    return true;
  }
  quarantineCustomerOwnedSessionStorage();
  return false;
}

/**
 * Reclaims only expired, uncommitted gateway state. A committed lineage or
 * any transition/lifecycle claim is durable authority and is never evicted.
 * Every eligible pointer is first serialized behind a persistent authority
 * claim: an absent slot uses strict create and a resolution uses exact replace.
 * Only then may the intent be removed, followed by the exact claim. This keeps
 * a concurrent resolution creator from occupying the slot between a snapshot
 * or removal and the destructive intent write.
 */
export function reclaimExpiredSpecialistLocationGatewayPreDraftCapacityStrict(
  input: { nowEpochMs?: number; excludingDraftId?: string } = {},
): ReclaimExpiredSpecialistLocationGatewayPreDraftCapacityResult {
  const nowEpochMs = safeAuthorityEpoch(input.nowEpochMs);
  if (nowEpochMs === null) return { status: "none" };

  const intents = inspectSpecialistLocationGatewayIntentsStrict();
  const authorities = inspectAuthoritiesStrict();
  if (intents.status === "unreadable" || authorities.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    };
  }

  const authorityByDraftId = new Map(
    authorities.drafts.map((authority) => [authority.draftId, authority]),
  );
  const expiredIntents = intents.drafts
    .filter(
      (intent) =>
        intent.draftId !== input.excludingDraftId &&
        !isSpecialistLocationGatewayRecordFresh(intent, nowEpochMs),
    )
    .sort((left, right) =>
      left.expiresAtEpochMs === right.expiresAtEpochMs
        ? left.draftId.localeCompare(right.draftId)
        : left.expiresAtEpochMs - right.expiresAtEpochMs,
    );

  let reclaimedCount = 0;
  for (const intent of expiredIntents) {
    const authority = authorityByDraftId.get(intent.draftId);
    if (
      authority &&
      (authority.kind !==
        "specialist-location-gateway-pre-draft-resolution" ||
        isSpecialistLocationGatewayRecordFresh(authority, nowEpochMs) ||
        !resolutionMatchesIntentRecord(authority, intent))
    ) {
      continue;
    }

    const claim = createSpecialistLocationGatewayPreDraftGcClaim({
      intentRecord: intent,
      priorResolution: authority ?? null,
      claimedAtEpochMs: nowEpochMs,
    });
    if (!claim) {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    const acquired = authority
      ? replaceAuthorityStrict(authority, claim)
      : createAuthorityStrict(claim);
    const claimAcquired = authority
      ? acquired.status === "replaced"
      : acquired.status === "created";
    if (!claimAcquired) {
      if (ambiguous(acquired.status)) {
        quarantineCustomerOwnedSessionStorage();
        return {
          status: "recovery-required",
          reason: "ambiguous-persistence",
        };
      }
      if (
        acquired.status === "conflict" ||
        (!authority && acquired.status === "capacity")
      ) {
        continue;
      }
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }

    const removedIntent = removeSpecialistLocationGatewayIntentStrict(intent);
    if (removedIntent.status === "recovery-required") {
      return removedIntent;
    }
    if (
      removedIntent.status !== "removed" &&
      removedIntent.status !== "absent"
    ) {
      const intentAfterFailure =
        inspectSpecialistLocationGatewayIntentExactPersisted(intent);
      if (intentAfterFailure.status === "present-exact") {
        if (!compensateSpecialistLocationGatewayPreDraftGcClaimExact(claim)) {
          return {
            status: "recovery-required",
            reason: "ambiguous-persistence",
          };
        }
        continue;
      }
      if (intentAfterFailure.status !== "absent") {
        quarantineCustomerOwnedSessionStorage();
        return {
          status: "recovery-required",
          reason: "ambiguous-persistence",
        };
      }
    }

    const removedClaim = removeAuthorityStrict(claim);
    if (
      removedClaim.status !== "removed" &&
      removedClaim.status !== "absent"
    ) {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    reclaimedCount += 1;
  }

  return reclaimedCount > 0
    ? { status: "reclaimed", reclaimedCount }
    : { status: "none" };
}

function createCatalogTransitionClaim(input: {
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
  attemptId: string;
  targetCatalog: "packages" | "services";
  claimedAtEpochMs: number;
}) {
  const targetGeneration = input.resolution.generation + 1;
  if (
    !isSpecialistLocationGatewayIdentifier(input.attemptId) ||
    !Number.isSafeInteger(targetGeneration) ||
    !isSpecialistLocationGatewayRecordFresh(
      input.resolution,
      input.claimedAtEpochMs,
    ) ||
    (input.targetCatalog === "packages") !==
      (input.resolution.selection.authority.kind === "appointment")
  ) {
    return null;
  }
  const body = navigationBase(input.resolution);
  const claim: SpecialistLocationGatewayCatalogTransitionClaimV1 = {
    ...body,
    kind: "specialist-location-gateway-catalog-transition-claim",
    mutationRevision: 3,
    attemptId: input.attemptId,
    targetCatalog: input.targetCatalog,
    targetGeneration,
    claimedAtEpochMs: input.claimedAtEpochMs,
    ownerKey: createSpecialistLocationGatewayNavigationOwnerKey({
      phase: "catalog-transition",
      ...body,
      attemptId: input.attemptId,
      targetCatalog: input.targetCatalog,
      targetGeneration,
      claimedAtEpochMs: input.claimedAtEpochMs,
    }),
  };
  const validation = validateSpecialistLocationGatewayNavigationAuthority(
    claim,
  );
  return validation.valid &&
    validation.draft.kind ===
      "specialist-location-gateway-catalog-transition-claim"
    ? validation.draft
    : null;
}

function createCatalogTransitionIntent(input: {
  currentIntent: SpecialistLocationGatewayIntentRecordV1;
  targetCatalog: "packages" | "services";
  claimedAtEpochMs: number;
}) {
  if (
    input.currentIntent.generation >= Number.MAX_SAFE_INTEGER ||
    !isSpecialistLocationGatewayRecordFresh(
      input.currentIntent,
      input.claimedAtEpochMs,
    )
  ) {
    return null;
  }
  try {
    const next = createSpecialistLocationGatewayIntentRecord({
      draftId: input.currentIntent.draftId,
      gatewayProviderId: input.currentIntent.gatewayProviderId,
      gatewayProviderRevision: input.currentIntent.gatewayProviderRevision,
      customerAccountId: input.currentIntent.customerAccountId,
      locale: input.currentIntent.locale,
      generation: input.currentIntent.generation + 1,
      createdAtEpochMs: input.claimedAtEpochMs,
      expiresAtEpochMs: input.currentIntent.expiresAtEpochMs,
      sourceAction: input.currentIntent.sourceAction,
      source: input.currentIntent.source,
      intent: { kind: "catalog", initialCatalog: input.targetCatalog },
    });
    const validation = validateSpecialistLocationGatewayIntentRecord(next);
    return validation.valid ? validation.draft : null;
  } catch {
    return null;
  }
}

export type TransitionSpecialistLocationGatewayCatalogResult =
  | {
      status: "transitioned";
      intent: SpecialistLocationGatewayIntentRecordV1;
      resolution: SpecialistLocationGatewayPreDraftResolutionV1;
    }
  | {
      status: "failed";
      reason:
        | "aborted"
        | "invalid-input"
        | "owner-drift"
        | "provider-unavailable";
    }
  | {
      status: "recovery-required";
      reason: "ambiguous-persistence";
    };

function restoreCatalogTransitionAuthority(input: {
  claim: SpecialistLocationGatewayCatalogTransitionClaimV1;
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
}) {
  const restored = replaceAuthorityStrict(input.claim, input.resolution);
  if (restored.status === "replaced") return true;
  quarantineCustomerOwnedSessionStorage();
  return false;
}

function rollbackCatalogTransition(input: {
  claim: SpecialistLocationGatewayCatalogTransitionClaimV1;
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
  currentIntent: SpecialistLocationGatewayIntentRecordV1;
  transitionedIntent: SpecialistLocationGatewayIntentRecordV1;
}) {
  const intentRollback = compareAndSwapSpecialistLocationGatewayIntentStrict({
    expected: input.transitionedIntent,
    next: input.currentIntent,
  });
  if (intentRollback.status !== "replaced") {
    quarantineCustomerOwnedSessionStorage();
    return false;
  }
  return restoreCatalogTransitionAuthority(input);
}

/**
 * Store-owned, no-domain-draft catalog switch. The resolution -> transition
 * claim CAS serializes against Continue and sibling switches. The intent is
 * then advanced to the truthful target catalog and only a fresh exact-B
 * provider publication may replace the claim with the next resolution.
 */
export async function transitionSpecialistLocationGatewayCatalog(input: {
  currentIntent: SpecialistLocationGatewayIntentRecordV1;
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
  targetCatalog: "packages" | "services";
  attemptId: string;
  claimedAtEpochMs: number;
  provider: SpecialistLocationGatewayProviderV1;
  signal: AbortSignal;
  now: () => number;
  isCurrentOwner: () => boolean;
}): Promise<TransitionSpecialistLocationGatewayCatalogResult> {
  const intentValidation = validateSpecialistLocationGatewayIntentRecord(
    input.currentIntent,
  );
  const resolutionValidation =
    validateSpecialistLocationGatewayNavigationAuthority(input.resolution);
  if (
    !intentValidation.valid ||
    !resolutionValidation.valid ||
    resolutionValidation.draft.kind !==
      "specialist-location-gateway-pre-draft-resolution" ||
    !resolutionMatchesIntentRecord(
      resolutionValidation.draft,
      intentValidation.draft,
    ) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  const currentIntent = intentValidation.draft;
  const resolution = resolutionValidation.draft;
  const claim = createCatalogTransitionClaim({ ...input, resolution });
  const transitionedIntent = createCatalogTransitionIntent({
    ...input,
    currentIntent,
  });
  if (!claim || !transitionedIntent) {
    return { status: "failed", reason: "invalid-input" };
  }

  const persistedBefore = inspectAuthorityStrict(resolution.draftId);
  if (
    persistedBefore.status !== "present" ||
    JSON.stringify(persistedBefore.draft) !== JSON.stringify(resolution)
  ) {
    if (persistedBefore.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }
  const claimed = replaceAuthorityStrict(resolution, claim);
  if (
    claimed.status !== "replaced" ||
    claimed.draft.kind !==
      "specialist-location-gateway-catalog-transition-claim"
  ) {
    if (ambiguous(claimed.status)) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }
  const persistedClaim = claimed.draft;
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    if (!restoreCatalogTransitionAuthority({ claim: persistedClaim, resolution })) {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }

  const intentTransition =
    compareAndSwapSpecialistLocationGatewayIntentStrict({
      expected: currentIntent,
      next: transitionedIntent,
    });
  if (intentTransition.status !== "replaced") {
    if (!restoreCatalogTransitionAuthority({ claim: persistedClaim, resolution })) {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return intentTransition.status === "recovery-required"
      ? { status: "recovery-required", reason: "ambiguous-persistence" }
      : { status: "failed", reason: "owner-drift" };
  }
  const persistedIntent = intentTransition.intent;

  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    if (
      !rollbackCatalogTransition({
        claim: persistedClaim,
        resolution,
        currentIntent,
        transitionedIntent: persistedIntent,
      })
    ) {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }

  const resolved = await resolveSpecialistLocationGateway({
    intent: persistedIntent,
    provider: input.provider,
    signal: input.signal,
    now: input.now,
    isCurrentHandoff: (candidate) =>
      candidate.ownerKey === persistedIntent.ownerKey &&
      operationIsCurrent(input.signal, input.isCurrentOwner),
  });
  if (
    resolved.status !== "ready" ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    if (
      !rollbackCatalogTransition({
        claim: persistedClaim,
        resolution,
        currentIntent,
        transitionedIntent: persistedIntent,
      })
    ) {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted
        ? "aborted"
        : resolved.status === "ready"
          ? "owner-drift"
          : "provider-unavailable",
    };
  }

  const rows = resolved.publication.rows.filter(
    (row) =>
      row.locationId === resolution.selection.selectedLocationId &&
      row.userLocationId === resolution.selection.selectedUserLocationId,
  );
  let transitionNow: number;
  try {
    transitionNow = input.now();
  } catch {
    transitionNow = -1;
  }
  const nextResolution =
    rows.length === 1 && operationIsCurrent(input.signal, input.isCurrentOwner)
      ? createSpecialistLocationGatewayPreDraftResolution({
          resolutionId: resolution.draftId,
          intentRecord: persistedIntent,
          publication: resolved.publication,
          selectedRow: rows[0]!,
          nowEpochMs: transitionNow,
        })
      : null;
  if (
    !nextResolution ||
    nextResolution.selection.selectedLocationId !==
      resolution.selection.selectedLocationId ||
    nextResolution.selection.selectedUserLocationId !==
      resolution.selection.selectedUserLocationId ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    if (
      !rollbackCatalogTransition({
        claim: persistedClaim,
        resolution,
        currentIntent,
        transitionedIntent: persistedIntent,
      })
    ) {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "provider-unavailable",
    };
  }

  const completed = replaceAuthorityStrict(persistedClaim, nextResolution);
  if (
    completed.status === "replaced" &&
    completed.draft.kind ===
      "specialist-location-gateway-pre-draft-resolution"
  ) {
    return {
      status: "transitioned",
      intent: persistedIntent,
      resolution: completed.draft,
    };
  }
  quarantineCustomerOwnedSessionStorage();
  return { status: "recovery-required", reason: "ambiguous-persistence" };
}

export interface SpecialistLocationGatewayDestinationDraftProof {
  destinationDraftId: string;
  persistedFingerprint: string;
}

type SpecialistLocationGatewayDestinationCreateInspection =
  | ({ status: "created" | "present-exact" } &
      SpecialistLocationGatewayDestinationDraftProof)
  | { status: "failed" }
  | { status: "unreadable" };

type SpecialistLocationGatewayDestinationRemovalInspection =
  | ({ status: "absent" | "removed" } &
      SpecialistLocationGatewayDestinationDraftProof)
  | { status: "failed" }
  | { status: "unreadable" };

function snapshotExactExternalRecord(
  value: unknown,
  expectedKeys: readonly string[],
): Record<string, unknown> | null {
  try {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return null;
    }
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record);
    if (
      keys.length !== expectedKeys.length ||
      !keys.every((key) => expectedKeys.includes(key))
    ) {
      return null;
    }
    const snapshot: Record<string, unknown> = {};
    for (const key of expectedKeys) snapshot[key] = record[key];
    return snapshot;
  } catch {
    return null;
  }
}

function snapshotDestinationCreateInspection(
  value: unknown,
): SpecialistLocationGatewayDestinationCreateInspection | null {
  const statusOnly = snapshotExactExternalRecord(value, ["status"]);
  if (
    statusOnly?.status === "failed" ||
    statusOnly?.status === "unreadable"
  ) {
    return { status: statusOnly.status };
  }
  const proof = snapshotExactExternalRecord(value, [
    "status",
    "destinationDraftId",
    "persistedFingerprint",
  ]);
  if (
    (proof?.status !== "created" && proof?.status !== "present-exact") ||
    !isSpecialistLocationGatewayIdentifier(proof.destinationDraftId) ||
    !isSpecialistLocationGatewayFingerprint(proof.persistedFingerprint)
  ) {
    return null;
  }
  return {
    status: proof.status,
    destinationDraftId: proof.destinationDraftId,
    persistedFingerprint: proof.persistedFingerprint,
  };
}

function snapshotDestinationRemovalInspection(
  value: unknown,
): SpecialistLocationGatewayDestinationRemovalInspection | null {
  const statusOnly = snapshotExactExternalRecord(value, ["status"]);
  if (
    statusOnly?.status === "failed" ||
    statusOnly?.status === "unreadable"
  ) {
    return { status: statusOnly.status };
  }
  const proof = snapshotExactExternalRecord(value, [
    "status",
    "destinationDraftId",
    "persistedFingerprint",
  ]);
  if (
    (proof?.status !== "absent" && proof?.status !== "removed") ||
    !isSpecialistLocationGatewayIdentifier(proof.destinationDraftId) ||
    !isSpecialistLocationGatewayFingerprint(proof.persistedFingerprint)
  ) {
    return null;
  }
  return {
    status: proof.status,
    destinationDraftId: proof.destinationDraftId,
    persistedFingerprint: proof.persistedFingerprint,
  };
}

export interface SpecialistLocationGatewayDestinationDraftTransaction {
  createOrInspectExact(): Promise<
    | ({ status: "created" | "present-exact" } &
        SpecialistLocationGatewayDestinationDraftProof)
    | { status: "failed" }
    | { status: "unreadable" }
  >;
  removeExact(
    proof: SpecialistLocationGatewayDestinationDraftProof,
  ): Promise<
    | ({ status: "absent" | "removed" } &
        SpecialistLocationGatewayDestinationDraftProof)
    | { status: "failed" }
    | { status: "unreadable" }
  >;
}

export interface SpecialistLocationGatewayAuthenticatedDestinationProof {
  destinationDraftId: string;
  priorPersistedFingerprint: string;
  authenticatedPersistedFingerprint: string;
  authenticatedCustomerAccountId: string;
}

export interface SpecialistLocationGatewayAuthenticatedDestinationInspector {
  inspectAuthenticatedExact(): Promise<
    | ({ status: "present-exact" } &
        SpecialistLocationGatewayAuthenticatedDestinationProof)
    | { status: "absent" | "failed" | "unreadable" }
  >;
}

export interface SpecialistLocationGatewayPostDraftDestinationInspector {
  inspectExact(): Promise<
    | ({ status: "present-exact" } &
        SpecialistLocationGatewayDestinationDraftProof)
    | { status: "absent" | "failed" | "unreadable" }
  >;
}

export interface SpecialistLocationGatewayDurableDestinationOwnerBinding
  extends SpecialistLocationGatewayAuthenticatedDestinationInspector {
  bindAuthenticatedOwnerExact(input: {
    destinationDraftId: string;
    expectedPriorPersistedFingerprint: string;
    expectedAuthenticatedPersistedFingerprint: string;
    authenticatedCustomerAccountId: string;
  }): Promise<
    | ({ status: "bound" | "present-exact" } &
        SpecialistLocationGatewayAuthenticatedDestinationProof)
    | { status: "absent" | "failed" | "unreadable" }
  >;
  restoreAnonymousOwnerExact(
    proof: SpecialistLocationGatewayAuthenticatedDestinationProof,
  ): Promise<
    | ({ status: "restored" | "present-exact" } &
        SpecialistLocationGatewayAuthenticatedDestinationProof)
    | { status: "absent" | "failed" | "unreadable" }
  >;
}

type SpecialistLocationGatewayAuthenticatedDestinationInspection =
  | ({ status: "present-exact" } &
      SpecialistLocationGatewayAuthenticatedDestinationProof)
  | { status: "absent" }
  | { status: "failed" }
  | { status: "unreadable" };

function snapshotAuthenticatedDestinationInspection(
  value: unknown,
): SpecialistLocationGatewayAuthenticatedDestinationInspection | null {
  const statusOnly = snapshotExactExternalRecord(value, ["status"]);
  if (
    statusOnly?.status === "absent" ||
    statusOnly?.status === "failed" ||
    statusOnly?.status === "unreadable"
  ) {
    return { status: statusOnly.status };
  }
  const proof = snapshotExactExternalRecord(value, [
    "status",
    "destinationDraftId",
    "priorPersistedFingerprint",
    "authenticatedPersistedFingerprint",
    "authenticatedCustomerAccountId",
  ]);
  if (
    proof?.status !== "present-exact" ||
    !isSpecialistLocationGatewayIdentifier(proof.destinationDraftId) ||
    !isSpecialistLocationGatewayFingerprint(
      proof.priorPersistedFingerprint,
    ) ||
    !isSpecialistLocationGatewayFingerprint(
      proof.authenticatedPersistedFingerprint,
    ) ||
    !isSpecialistLocationGatewayIdentifier(
      proof.authenticatedCustomerAccountId,
    )
  ) {
    return null;
  }
  return {
    status: "present-exact",
    destinationDraftId: proof.destinationDraftId,
    priorPersistedFingerprint: proof.priorPersistedFingerprint,
    authenticatedPersistedFingerprint:
      proof.authenticatedPersistedFingerprint,
    authenticatedCustomerAccountId:
      proof.authenticatedCustomerAccountId,
  };
}

type SpecialistLocationGatewayPostDraftDestinationInspection =
  | ({ status: "present-exact" } &
      SpecialistLocationGatewayDestinationDraftProof)
  | { status: "absent" }
  | { status: "failed" }
  | { status: "unreadable" };

function snapshotPostDraftDestinationInspection(
  value: unknown,
): SpecialistLocationGatewayPostDraftDestinationInspection | null {
  const statusOnly = snapshotExactExternalRecord(value, ["status"]);
  if (
    statusOnly?.status === "absent" ||
    statusOnly?.status === "failed" ||
    statusOnly?.status === "unreadable"
  ) {
    return { status: statusOnly.status };
  }
  const proof = snapshotExactExternalRecord(value, [
    "status",
    "destinationDraftId",
    "persistedFingerprint",
  ]);
  if (
    proof?.status !== "present-exact" ||
    !isSpecialistLocationGatewayIdentifier(proof.destinationDraftId) ||
    !isSpecialistLocationGatewayFingerprint(proof.persistedFingerprint)
  ) {
    return null;
  }
  return {
    status: "present-exact",
    destinationDraftId: proof.destinationDraftId,
    persistedFingerprint: proof.persistedFingerprint,
  };
}

type SpecialistLocationGatewayDestinationOwnerMutationInspection =
  | ({ status: "bound" | "present-exact" } &
      SpecialistLocationGatewayAuthenticatedDestinationProof)
  | { status: "absent" }
  | { status: "failed" }
  | { status: "unreadable" };

function snapshotDestinationOwnerMutationInspection(
  value: unknown,
): SpecialistLocationGatewayDestinationOwnerMutationInspection | null {
  const statusOnly = snapshotExactExternalRecord(value, ["status"]);
  if (
    statusOnly?.status === "absent" ||
    statusOnly?.status === "failed" ||
    statusOnly?.status === "unreadable"
  ) {
    return { status: statusOnly.status };
  }
  const proof = snapshotExactExternalRecord(value, [
    "status",
    "destinationDraftId",
    "priorPersistedFingerprint",
    "authenticatedPersistedFingerprint",
    "authenticatedCustomerAccountId",
  ]);
  if (
    (proof?.status !== "bound" && proof?.status !== "present-exact") ||
    !isSpecialistLocationGatewayIdentifier(proof.destinationDraftId) ||
    !isSpecialistLocationGatewayFingerprint(
      proof.priorPersistedFingerprint,
    ) ||
    !isSpecialistLocationGatewayFingerprint(
      proof.authenticatedPersistedFingerprint,
    ) ||
    !isSpecialistLocationGatewayIdentifier(
      proof.authenticatedCustomerAccountId,
    )
  ) {
    return null;
  }
  return {
    status: proof.status,
    destinationDraftId: proof.destinationDraftId,
    priorPersistedFingerprint: proof.priorPersistedFingerprint,
    authenticatedPersistedFingerprint:
      proof.authenticatedPersistedFingerprint,
    authenticatedCustomerAccountId:
      proof.authenticatedCustomerAccountId,
  };
}

type SpecialistLocationGatewayDestinationOwnerRestorationInspection =
  | ({ status: "restored" | "present-exact" } &
      SpecialistLocationGatewayAuthenticatedDestinationProof)
  | { status: "absent" }
  | { status: "failed" }
  | { status: "unreadable" };

function snapshotDestinationOwnerRestorationInspection(
  value: unknown,
): SpecialistLocationGatewayDestinationOwnerRestorationInspection | null {
  const statusOnly = snapshotExactExternalRecord(value, ["status"]);
  if (
    statusOnly?.status === "absent" ||
    statusOnly?.status === "failed" ||
    statusOnly?.status === "unreadable"
  ) {
    return { status: statusOnly.status };
  }
  const proof = snapshotExactExternalRecord(value, [
    "status",
    "destinationDraftId",
    "priorPersistedFingerprint",
    "authenticatedPersistedFingerprint",
    "authenticatedCustomerAccountId",
  ]);
  if (
    (proof?.status !== "restored" && proof?.status !== "present-exact") ||
    !isSpecialistLocationGatewayIdentifier(proof.destinationDraftId) ||
    !isSpecialistLocationGatewayFingerprint(
      proof.priorPersistedFingerprint,
    ) ||
    !isSpecialistLocationGatewayFingerprint(
      proof.authenticatedPersistedFingerprint,
    ) ||
    !isSpecialistLocationGatewayIdentifier(
      proof.authenticatedCustomerAccountId,
    )
  ) {
    return null;
  }
  return {
    status: proof.status,
    destinationDraftId: proof.destinationDraftId,
    priorPersistedFingerprint: proof.priorPersistedFingerprint,
    authenticatedPersistedFingerprint:
      proof.authenticatedPersistedFingerprint,
    authenticatedCustomerAccountId:
      proof.authenticatedCustomerAccountId,
  };
}

export interface SpecialistLocationGatewayMutableDestinationProof {
  destinationDraftId: string;
  persistedFingerprint: string;
  mutableCleanupFingerprint: string;
}

export interface SpecialistLocationGatewayDestinationLifecycle {
  inspectMutableCleanupExact(): Promise<
    | ({ status: "mutable-cleanup-exact" } &
        SpecialistLocationGatewayMutableDestinationProof)
    | {
        status:
          | "absent"
          | "ambiguous"
          | "failed"
          | "terminal-or-ambiguous-blocked"
          | "unreadable";
      }
  >;
  removeMutableExact(
    proof: SpecialistLocationGatewayMutableDestinationProof,
  ): Promise<
    | ({ status: "removed" } &
        SpecialistLocationGatewayMutableDestinationProof)
    | { status: "absent" | "ambiguous" | "failed" | "unreadable" }
  >;
  restoreMutableExact(
    proof: SpecialistLocationGatewayMutableDestinationProof,
  ): Promise<
    | ({ status: "present-exact" | "restored" } &
        SpecialistLocationGatewayMutableDestinationProof)
    | { status: "ambiguous" | "failed" | "unreadable" }
  >;
}

type SpecialistLocationGatewayMutableDestinationInspection =
  | ({ status: "mutable-cleanup-exact" } &
      SpecialistLocationGatewayMutableDestinationProof)
  | { status: "absent" }
  | { status: "ambiguous" }
  | { status: "failed" }
  | { status: "terminal-or-ambiguous-blocked" }
  | { status: "unreadable" };

type SpecialistLocationGatewayMutableDestinationRemoval =
  | ({ status: "removed" } &
      SpecialistLocationGatewayMutableDestinationProof)
  | { status: "absent" }
  | { status: "ambiguous" }
  | { status: "failed" }
  | { status: "unreadable" };

type SpecialistLocationGatewayMutableDestinationRestoration =
  | ({ status: "present-exact" | "restored" } &
      SpecialistLocationGatewayMutableDestinationProof)
  | { status: "ambiguous" }
  | { status: "failed" }
  | { status: "unreadable" };

function snapshotMutableDestinationProof(
  value: unknown,
  acceptedStatuses: readonly string[],
) {
  const proof = snapshotExactExternalRecord(value, [
    "status",
    "destinationDraftId",
    "persistedFingerprint",
    "mutableCleanupFingerprint",
  ]);
  if (
    typeof proof?.status !== "string" ||
    !acceptedStatuses.includes(proof.status) ||
    !isSpecialistLocationGatewayIdentifier(proof.destinationDraftId) ||
    !isSpecialistLocationGatewayFingerprint(proof.persistedFingerprint) ||
    !isSpecialistLocationGatewayFingerprint(
      proof.mutableCleanupFingerprint,
    )
  ) {
    return null;
  }
  return {
    status: proof.status,
    destinationDraftId: proof.destinationDraftId,
    persistedFingerprint: proof.persistedFingerprint,
    mutableCleanupFingerprint: proof.mutableCleanupFingerprint,
  };
}

function snapshotMutableDestinationInspection(
  value: unknown,
): SpecialistLocationGatewayMutableDestinationInspection | null {
  const statusOnly = snapshotExactExternalRecord(value, ["status"]);
  if (
    statusOnly?.status === "absent" ||
    statusOnly?.status === "ambiguous" ||
    statusOnly?.status === "failed" ||
    statusOnly?.status === "terminal-or-ambiguous-blocked" ||
    statusOnly?.status === "unreadable"
  ) {
    return { status: statusOnly.status };
  }
  const proof = snapshotMutableDestinationProof(value, [
    "mutable-cleanup-exact",
  ]);
  return proof?.status === "mutable-cleanup-exact"
    ? { ...proof, status: "mutable-cleanup-exact" }
    : null;
}

function snapshotMutableDestinationRemoval(
  value: unknown,
): SpecialistLocationGatewayMutableDestinationRemoval | null {
  const statusOnly = snapshotExactExternalRecord(value, ["status"]);
  if (
    statusOnly?.status === "absent" ||
    statusOnly?.status === "ambiguous" ||
    statusOnly?.status === "failed" ||
    statusOnly?.status === "unreadable"
  ) {
    return { status: statusOnly.status };
  }
  const proof = snapshotMutableDestinationProof(value, ["removed"]);
  return proof?.status === "removed"
    ? { ...proof, status: "removed" }
    : null;
}

function snapshotMutableDestinationRestoration(
  value: unknown,
): SpecialistLocationGatewayMutableDestinationRestoration | null {
  const statusOnly = snapshotExactExternalRecord(value, ["status"]);
  if (
    statusOnly?.status === "ambiguous" ||
    statusOnly?.status === "failed" ||
    statusOnly?.status === "unreadable"
  ) {
    return { status: statusOnly.status };
  }
  const proof = snapshotMutableDestinationProof(value, [
    "present-exact",
    "restored",
  ]);
  return proof?.status === "present-exact" || proof?.status === "restored"
    ? { ...proof, status: proof.status }
    : null;
}

export type CommitSpecialistLocationGatewayLineageResult =
  | {
      status: "committed";
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    }
  | {
      status: "failed";
      reason:
        | "aborted"
        | "destination-create-failed"
        | "intent-consumed"
        | "invalid-input"
        | "owner-drift";
    }
  | {
      status: "recovery-required";
      reason: "ambiguous-persistence" | "compensation-failed";
      destinationDraftId: string;
    };

async function compensateDestination(input: {
  transaction: SpecialistLocationGatewayDestinationDraftTransaction;
  proof: SpecialistLocationGatewayDestinationDraftProof;
}) {
  let rawRemoved: unknown;
  try {
    rawRemoved = await input.transaction.removeExact(input.proof);
  } catch {
    return false;
  }
  const removed = snapshotDestinationRemovalInspection(rawRemoved);
  return (
    removed !== null &&
    (removed.status === "removed" || removed.status === "absent") &&
    removed.destinationDraftId === input.proof.destinationDraftId &&
    removed.persistedFingerprint === input.proof.persistedFingerprint
  );
}

function ownsCurrentCommit(
  isCurrentOwner: () => boolean,
) {
  try {
    return isCurrentOwner() === true;
  } catch {
    return false;
  }
}

function operationIsCurrent(
  signal: AbortSignal,
  isCurrentOwner: () => boolean,
) {
  if (signal.aborted) return false;
  if (!ownsCurrentCommit(isCurrentOwner)) return false;
  return !signal.aborted;
}

function mutableProofMatchesLineage(
  proof: SpecialistLocationGatewayMutableDestinationProof,
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1,
) {
  return (
    proof.destinationDraftId === lineage.destinationDraftId &&
    proof.persistedFingerprint === lineage.destinationDraftFingerprint
  );
}

async function inspectMutableDestinationExact(
  lifecycle: SpecialistLocationGatewayDestinationLifecycle,
) {
  let raw: unknown;
  try {
    raw = await lifecycle.inspectMutableCleanupExact();
  } catch {
    return null;
  }
  return snapshotMutableDestinationInspection(raw);
}

async function removeMutableDestinationExact(input: {
  lifecycle: SpecialistLocationGatewayDestinationLifecycle;
  proof: SpecialistLocationGatewayMutableDestinationProof;
}) {
  let raw: unknown;
  try {
    raw = await input.lifecycle.removeMutableExact(input.proof);
  } catch {
    return null;
  }
  return snapshotMutableDestinationRemoval(raw);
}

async function restoreMutableDestinationExact(input: {
  lifecycle: SpecialistLocationGatewayDestinationLifecycle;
  proof: SpecialistLocationGatewayMutableDestinationProof;
}) {
  let raw: unknown;
  try {
    raw = await input.lifecycle.restoreMutableExact(input.proof);
  } catch {
    return false;
  }
  const restored = snapshotMutableDestinationRestoration(raw);
  return Boolean(
    restored &&
      (restored.status === "restored" ||
        restored.status === "present-exact") &&
      restored.destinationDraftId === input.proof.destinationDraftId &&
      restored.persistedFingerprint === input.proof.persistedFingerprint &&
      restored.mutableCleanupFingerprint ===
        input.proof.mutableCleanupFingerprint,
  );
}

function createDestinationLifecycleClaim(input: {
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  lifecycleAttemptId: string;
  action: "close" | "return-to-pre-draft";
  proof: SpecialistLocationGatewayMutableDestinationProof;
  claimedAtEpochMs: number;
}) {
  if (
    !isSpecialistLocationGatewayIdentifier(input.lifecycleAttemptId) ||
    !mutableProofMatchesLineage(input.proof, input.lineage)
  ) {
    return null;
  }
  const body = navigationBase(input.lineage);
  const claim: SpecialistLocationGatewayDestinationLifecycleClaimV1 = {
    ...body,
    kind: "specialist-location-gateway-destination-lifecycle-claim",
    mutationRevision: 5,
    resolutionId: input.lineage.resolutionId,
    lineageAttemptId: input.lineage.attemptId,
    lifecycleAttemptId: input.lifecycleAttemptId,
    action: input.action,
    destinationKind: input.lineage.destinationKind,
    destinationDraftId: input.lineage.destinationDraftId,
    destinationDraftFingerprint:
      input.lineage.destinationDraftFingerprint,
    mutableCleanupFingerprint: input.proof.mutableCleanupFingerprint,
    lineageCommittedAtEpochMs: input.lineage.committedAtEpochMs,
    claimedAtEpochMs: input.claimedAtEpochMs,
    ownerKey: createSpecialistLocationGatewayNavigationOwnerKey({
      phase: "destination-lifecycle",
      ...body,
      resolutionId: input.lineage.resolutionId,
      lineageAttemptId: input.lineage.attemptId,
      lifecycleAttemptId: input.lifecycleAttemptId,
      lifecycleAction: input.action,
      destinationKind: input.lineage.destinationKind,
      destinationDraftId: input.lineage.destinationDraftId,
      destinationDraftFingerprint:
        input.lineage.destinationDraftFingerprint,
      mutableCleanupFingerprint: input.proof.mutableCleanupFingerprint,
      lineageCommittedAtEpochMs: input.lineage.committedAtEpochMs,
      claimedAtEpochMs: input.claimedAtEpochMs,
    }),
  };
  const validation = validateSpecialistLocationGatewayNavigationAuthority(
    claim,
  );
  return validation.valid &&
    validation.draft.kind ===
      "specialist-location-gateway-destination-lifecycle-claim"
    ? validation.draft
    : null;
}

function createRefreshedDestinationLifecycleIntent(input: {
  intent: SpecialistLocationGatewayIntentRecordV1;
  gatewayProviderId: string;
  gatewayProviderRevision: string;
  refreshedAtEpochMs: number;
}) {
  const generation = input.intent.generation + 1;
  const expiresAtEpochMs =
    input.refreshedAtEpochMs + SPECIALIST_LOCATION_GATEWAY_DEFAULT_TTL_MS;
  if (
    !Number.isSafeInteger(generation) ||
    !Number.isSafeInteger(expiresAtEpochMs) ||
    !isSpecialistLocationGatewayIdentifier(input.gatewayProviderId) ||
    !isSpecialistLocationGatewayIdentifier(input.gatewayProviderRevision)
  ) {
    return null;
  }
  try {
    const candidate = createSpecialistLocationGatewayIntentRecord({
      draftId: input.intent.draftId,
      gatewayProviderId: input.gatewayProviderId,
      gatewayProviderRevision: input.gatewayProviderRevision,
      customerAccountId: input.intent.customerAccountId,
      locale: input.intent.locale,
      generation,
      createdAtEpochMs: input.refreshedAtEpochMs,
      expiresAtEpochMs,
      sourceAction: input.intent.sourceAction,
      source: input.intent.source,
      intent: input.intent.intent,
    });
    const validation = validateSpecialistLocationGatewayIntentRecord(candidate);
    return validation.valid ? validation.draft : null;
  } catch {
    return null;
  }
}

async function compensateDestinationLifecycle(input: {
  lifecycle: SpecialistLocationGatewayDestinationLifecycle;
  proof: SpecialistLocationGatewayMutableDestinationProof;
  claim: SpecialistLocationGatewayDestinationLifecycleClaimV1;
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
}) {
  const destinationRestored = await restoreMutableDestinationExact(input);
  const lineageRestored = replaceAuthorityStrict(input.claim, input.lineage);
  if (destinationRestored && lineageRestored.status === "replaced") {
    return true;
  }
  quarantineCustomerOwnedSessionStorage();
  return false;
}

async function compensateAfterLifecycleAuthorityRemoval(input: {
  lifecycle: SpecialistLocationGatewayDestinationLifecycle;
  proof: SpecialistLocationGatewayMutableDestinationProof;
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
}) {
  const destinationRestored = await restoreMutableDestinationExact(input);
  const authorityRestored = createAuthorityStrict(input.lineage);
  const authorityIsExact =
    authorityRestored.status === "created" ||
    (authorityRestored.status === "conflict" &&
      (() => {
        const inspected = inspectAuthorityStrict(input.lineage.draftId);
        return (
          inspected.status === "present" &&
          JSON.stringify(inspected.draft) === JSON.stringify(input.lineage)
        );
      })());
  if (destinationRestored && authorityIsExact) return true;
  quarantineCustomerOwnedSessionStorage();
  return false;
}

async function rollbackPostDestinationFailure(input: {
  destinationWasCreated: boolean;
  transaction: SpecialistLocationGatewayDestinationDraftTransaction;
  proof: SpecialistLocationGatewayDestinationDraftProof;
  persistedClaim: SpecialistLocationGatewayNavigationCommitClaimV1;
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
  destinationDraftId: string;
  failureReason: "aborted" | "invalid-input" | "owner-drift";
}): Promise<CommitSpecialistLocationGatewayLineageResult> {
  // An exact pre-existing draft belongs to a prior idempotent attempt. This
  // attempt has no deletion provenance, so recovery must preserve both bytes.
  if (!input.destinationWasCreated) {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
      destinationDraftId: input.destinationDraftId,
    };
  }
  const compensated = await compensateDestination({
    transaction: input.transaction,
    proof: input.proof,
  });
  const restored = replaceAuthorityStrict(
    input.persistedClaim,
    input.resolution,
  );
  if (!compensated || restored.status !== "replaced") {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "compensation-failed",
      destinationDraftId: input.destinationDraftId,
    };
  }
  return { status: "failed", reason: input.failureReason };
}

/**
 * Exact CAS resolution -> claim is the single-winner serialization point.
 * Domain persistence begins only after that claim; ambiguous writes quarantine.
 */
export async function commitSpecialistLocationGatewayNavigationLineage(input: {
  resolution: SpecialistLocationGatewayPreDraftResolutionV1;
  revalidatedResolution: SpecialistLocationGatewayPreDraftResolutionV1;
  attemptId: string;
  destinationKind: SpecialistLocationGatewayDestinationKind;
  destinationDraftId: string;
  expectedDestinationFingerprint: string;
  claimedAtEpochMs: number;
  now: () => number;
  signal: AbortSignal;
  /**
   * Must synchronously re-read the exact handoff/customer/locale/generation
   * owner and current provider identity. It is checked before the claim and
   * again after every external draft await.
   */
  isCurrentOwner: () => boolean;
  destinationDraft: SpecialistLocationGatewayDestinationDraftTransaction;
}): Promise<CommitSpecialistLocationGatewayLineageResult> {
  if (
    JSON.stringify(input.resolution) !==
      JSON.stringify(input.revalidatedResolution) ||
    !isSpecialistLocationGatewayFingerprint(
      input.expectedDestinationFingerprint,
    ) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  const claim = createCommitClaim(input);
  if (!claim) return { status: "failed", reason: "invalid-input" };

  const persistedBefore = inspectAuthorityStrict(
    input.resolution.draftId,
  );
  if (
    persistedBefore.status !== "present" ||
    JSON.stringify(persistedBefore.draft) !== JSON.stringify(input.resolution)
  ) {
    if (persistedBefore.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
        destinationDraftId: input.destinationDraftId,
      };
    }
    return { status: "failed", reason: "intent-consumed" };
  }

  const claimed = replaceAuthorityStrict(
    input.resolution,
    claim,
  );
  if (claimed.status !== "replaced") {
    if (ambiguous(claimed.status)) {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
        destinationDraftId: input.destinationDraftId,
      };
    }
    return { status: "failed", reason: "intent-consumed" };
  }
  if (
    claimed.draft.kind !==
    "specialist-location-gateway-navigation-commit-claim"
  ) {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
      destinationDraftId: input.destinationDraftId,
    };
  }
  const persistedClaim = claimed.draft;

  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    const restored = replaceAuthorityStrict(
      persistedClaim,
      input.resolution,
    );
    if (restored.status !== "replaced") {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
        destinationDraftId: input.destinationDraftId,
      };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }

  let rawDestination: unknown;
  try {
    rawDestination = await input.destinationDraft.createOrInspectExact();
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
      destinationDraftId: input.destinationDraftId,
    };
  }
  const destination = snapshotDestinationCreateInspection(rawDestination);
  if (!destination || destination.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
      destinationDraftId: input.destinationDraftId,
    };
  }
  if (destination.status === "failed") {
    const restored = replaceAuthorityStrict(
      persistedClaim,
      input.resolution,
    );
    if (restored.status !== "replaced") {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
        destinationDraftId: input.destinationDraftId,
      };
    }
    return { status: "failed", reason: "destination-create-failed" };
  }
  if (
    destination.destinationDraftId !== input.destinationDraftId ||
    destination.persistedFingerprint !== input.expectedDestinationFingerprint
  ) {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
      destinationDraftId: input.destinationDraftId,
    };
  }

  const proof: SpecialistLocationGatewayDestinationDraftProof = {
    destinationDraftId: destination.destinationDraftId,
    persistedFingerprint: destination.persistedFingerprint,
  };
  const destinationWasCreated = destination.status === "created";
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return rollbackPostDestinationFailure({
      destinationWasCreated,
      transaction: input.destinationDraft,
      proof,
      persistedClaim,
      resolution: input.resolution,
      destinationDraftId: input.destinationDraftId,
      failureReason: input.signal.aborted ? "aborted" : "owner-drift",
    });
  }

  let committedAtEpochMs: number;
  try {
    committedAtEpochMs = input.now();
  } catch {
    committedAtEpochMs = -1;
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return rollbackPostDestinationFailure({
      destinationWasCreated,
      transaction: input.destinationDraft,
      proof,
      persistedClaim,
      resolution: input.resolution,
      destinationDraftId: input.destinationDraftId,
      failureReason: input.signal.aborted ? "aborted" : "owner-drift",
    });
  }
  const lineage = createCommittedLineage({
    claim: persistedClaim,
    committedAtEpochMs,
  });
  if (!lineage) {
    return rollbackPostDestinationFailure({
      destinationWasCreated,
      transaction: input.destinationDraft,
      proof,
      persistedClaim,
      resolution: input.resolution,
      destinationDraftId: input.destinationDraftId,
      failureReason: "invalid-input",
    });
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return rollbackPostDestinationFailure({
      destinationWasCreated,
      transaction: input.destinationDraft,
      proof,
      persistedClaim,
      resolution: input.resolution,
      destinationDraftId: input.destinationDraftId,
      failureReason: input.signal.aborted ? "aborted" : "owner-drift",
    });
  }

  const committed = replaceAuthorityStrict(
    persistedClaim,
    lineage,
  );
  if (
    committed.status === "replaced" &&
    committed.draft.kind ===
      "specialist-location-gateway-navigation-lineage"
  ) {
    return { status: "committed", lineage: committed.draft };
  }
  const inspected = inspectAuthorityStrict(lineage.draftId);
  if (
    inspected.status === "present" &&
    JSON.stringify(inspected.draft) === JSON.stringify(lineage)
  ) {
    return { status: "committed", lineage };
  }
  quarantineCustomerOwnedSessionStorage();
  return {
    status: "recovery-required",
    reason: "ambiguous-persistence",
    destinationDraftId: input.destinationDraftId,
  };
}

function createAuthenticatedCommittedLineage(input: {
  anonymousLineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  authenticatedResolution: SpecialistLocationGatewayPreDraftResolutionV1;
  authenticatedDestinationFingerprint: string;
  committedAtEpochMs: number;
}) {
  if (
    !isSpecialistLocationGatewayFingerprint(
      input.authenticatedDestinationFingerprint,
    ) ||
    !isSpecialistLocationGatewayRecordFresh(
      input.authenticatedResolution,
      input.committedAtEpochMs,
    )
  ) {
    return null;
  }
  const body = {
    ...navigationBase(input.authenticatedResolution),
    resolutionId: input.anonymousLineage.resolutionId,
    attemptId: input.anonymousLineage.attemptId,
    destinationKind: input.anonymousLineage.destinationKind,
    destinationDraftId: input.anonymousLineage.destinationDraftId,
    destinationDraftFingerprint:
      input.authenticatedDestinationFingerprint,
  };
  const lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 = {
    ...body,
    kind: "specialist-location-gateway-navigation-lineage",
    mutationRevision: 2,
    committedAtEpochMs: input.committedAtEpochMs,
    ownerKey: createSpecialistLocationGatewayNavigationOwnerKey({
      phase: "committed",
      ...body,
      committedAtEpochMs: input.committedAtEpochMs,
    }),
  };
  const validation = validateSpecialistLocationGatewayNavigationAuthority(
    lineage,
  );
  return validation.valid &&
    validation.draft.kind ===
      "specialist-location-gateway-navigation-lineage"
    ? validation.draft
    : null;
}

type BindSpecialistLocationGatewayCommittedLineageAfterAuthResult =
  | {
      status: "bound";
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    }
  | {
      status: "failed";
      reason:
        | "aborted"
        | "already-owned"
        | "destination-mismatch"
        | "invalid-input"
        | "owner-drift"
        | "provider-unavailable"
        | "selection-drift";
    }
  | {
      status: "recovery-required";
      reason: "ambiguous-persistence";
    };

/**
 * Rebinds only the companion lineage after the destination domain draft has
 * completed its own exact anonymous -> authenticated mutation. The terminal
 * attempt/destination ID pair is immutable; a changed destination fingerprint
 * is accepted only from the exact inspector's prior -> authenticated proof.
 */
async function bindSpecialistLocationGatewayCommittedLineageAfterAuthStrict(
  input: {
    anonymousLineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    authenticatedIntent: SpecialistLocationGatewayIntentRecordV1;
    expectedPriorDestinationFingerprint: string;
    expectedAuthenticatedDestinationFingerprint: string;
    provider: SpecialistLocationGatewayProviderV1;
    destinationDraft: SpecialistLocationGatewayAuthenticatedDestinationInspector;
    signal: AbortSignal;
    now: () => number;
    isCurrentOwner: () => boolean;
  },
): Promise<BindSpecialistLocationGatewayCommittedLineageAfterAuthResult> {
  let lineageValidation: ReturnType<
    typeof validateSpecialistLocationGatewayNavigationAuthority
  >;
  let intentValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    lineageValidation = validateSpecialistLocationGatewayNavigationAuthority(
      input.anonymousLineage,
    );
    intentValidation = validateSpecialistLocationGatewayIntentRecord(
      input.authenticatedIntent,
    );
  } catch {
    return { status: "failed", reason: "invalid-input" };
  }
  if (
    !lineageValidation.valid ||
    lineageValidation.draft.kind !==
      "specialist-location-gateway-navigation-lineage" ||
    !intentValidation.valid ||
    !isSpecialistLocationGatewayFingerprint(
      input.expectedPriorDestinationFingerprint,
    ) ||
    !isSpecialistLocationGatewayFingerprint(
      input.expectedAuthenticatedDestinationFingerprint,
    )
  ) {
    return { status: "failed", reason: "invalid-input" };
  }
  const anonymousLineage = lineageValidation.draft;
  const authenticatedIntent = intentValidation.draft;
  if (anonymousLineage.customerAccountId !== null) {
    return { status: "failed", reason: "already-owned" };
  }
  if (
    authenticatedIntent.customerAccountId === null ||
    authenticatedIntent.draftId !== anonymousLineage.intentDraftId ||
    authenticatedIntent.generation !== anonymousLineage.generation + 1 ||
    authenticatedIntent.gatewayProviderId !==
      anonymousLineage.gatewayProviderId ||
    authenticatedIntent.gatewayProviderRevision !==
      anonymousLineage.gatewayProviderRevision ||
    authenticatedIntent.locale !== anonymousLineage.locale ||
    authenticatedIntent.sourceAction !== anonymousLineage.sourceAction ||
    JSON.stringify(authenticatedIntent.source) !==
      JSON.stringify(anonymousLineage.source) ||
    JSON.stringify(authenticatedIntent.intent) !==
      JSON.stringify(anonymousLineage.intent) ||
    anonymousLineage.destinationDraftFingerprint !==
      input.expectedPriorDestinationFingerprint ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  const persistedLineage = inspectAuthorityStrict(anonymousLineage.draftId);
  if (
    persistedLineage.status !== "present" ||
    JSON.stringify(persistedLineage.draft) !== JSON.stringify(anonymousLineage)
  ) {
    if (persistedLineage.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }
  let startedAtEpochMs: number;
  try {
    startedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "provider-unavailable" };
  }
  const storedIntent = readSpecialistLocationGatewayIntentStrict({
    handoffId: authenticatedIntent.draftId,
    nowEpochMs: startedAtEpochMs,
    expectedCustomerAccountId: authenticatedIntent.customerAccountId,
    expectedLocale: authenticatedIntent.locale,
    expectedSourceLocationId: authenticatedIntent.source.sourceLocationId,
    expectedSourceUserLocationId:
      authenticatedIntent.source.sourceUserLocationId,
  });
  if (
    storedIntent.status !== "ready" ||
    JSON.stringify(storedIntent.intent) !== JSON.stringify(authenticatedIntent)
  ) {
    return { status: "failed", reason: "owner-drift" };
  }
  const resolved = await resolveSpecialistLocationGateway({
    intent: authenticatedIntent,
    provider: input.provider,
    signal: input.signal,
    now: input.now,
    isCurrentHandoff: (candidate) =>
      candidate.ownerKey === authenticatedIntent.ownerKey &&
      operationIsCurrent(input.signal, input.isCurrentOwner),
  });
  if (resolved.status !== "ready") {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "provider-unavailable",
    };
  }
  const selectedRows = resolved.publication.rows.filter(
    (row) =>
      row.locationId === anonymousLineage.selection.selectedLocationId &&
      row.userLocationId ===
        anonymousLineage.selection.selectedUserLocationId,
  );
  let rawProof: unknown;
  try {
    rawProof = await input.destinationDraft.inspectAuthenticatedExact();
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  const proof = snapshotAuthenticatedDestinationInspection(rawProof);
  if (!proof || proof.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (proof.status !== "present-exact") {
    return { status: "failed", reason: "destination-mismatch" };
  }
  if (
    proof.destinationDraftId !== anonymousLineage.destinationDraftId ||
    proof.priorPersistedFingerprint !==
      input.expectedPriorDestinationFingerprint ||
    proof.authenticatedPersistedFingerprint !==
      input.expectedAuthenticatedDestinationFingerprint ||
    proof.authenticatedCustomerAccountId !==
      authenticatedIntent.customerAccountId
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  let completedAtEpochMs: number;
  try {
    completedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "provider-unavailable" };
  }
  const authenticatedResolution =
    selectedRows.length === 1 &&
    operationIsCurrent(input.signal, input.isCurrentOwner)
      ? createSpecialistLocationGatewayPreDraftResolution({
          resolutionId: anonymousLineage.resolutionId,
          intentRecord: authenticatedIntent,
          publication: resolved.publication,
          selectedRow: selectedRows[0]!,
          nowEpochMs: completedAtEpochMs,
        })
      : null;
  if (
    !authenticatedResolution ||
    JSON.stringify(authenticatedResolution.selection) !==
      JSON.stringify(anonymousLineage.selection)
  ) {
    return { status: "failed", reason: "selection-drift" };
  }
  let rawFinalProof: unknown;
  try {
    rawFinalProof = await input.destinationDraft.inspectAuthenticatedExact();
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  const finalProof = snapshotAuthenticatedDestinationInspection(rawFinalProof);
  if (
    !finalProof ||
    finalProof.status !== "present-exact" ||
    JSON.stringify(finalProof) !== JSON.stringify(proof)
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  let committedAtEpochMs: number;
  try {
    committedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "provider-unavailable" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const intentAfter = readSpecialistLocationGatewayIntentStrict({
    handoffId: authenticatedIntent.draftId,
    nowEpochMs: committedAtEpochMs,
    expectedCustomerAccountId: authenticatedIntent.customerAccountId,
    expectedLocale: authenticatedIntent.locale,
    expectedSourceLocationId: authenticatedIntent.source.sourceLocationId,
    expectedSourceUserLocationId:
      authenticatedIntent.source.sourceUserLocationId,
  });
  const authorityAfter = inspectAuthorityStrict(anonymousLineage.draftId);
  if (
    intentAfter.status !== "ready" ||
    JSON.stringify(intentAfter.intent) !== JSON.stringify(authenticatedIntent) ||
    authorityAfter.status !== "present" ||
    JSON.stringify(authorityAfter.draft) !== JSON.stringify(anonymousLineage)
  ) {
    return { status: "failed", reason: "owner-drift" };
  }
  const nextLineage = createAuthenticatedCommittedLineage({
    anonymousLineage,
    authenticatedResolution,
    authenticatedDestinationFingerprint:
      input.expectedAuthenticatedDestinationFingerprint,
    committedAtEpochMs,
  });
  if (!nextLineage || !operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  let rawPreCasProof: unknown;
  try {
    rawPreCasProof = await input.destinationDraft.inspectAuthenticatedExact();
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  const preCasProof = snapshotAuthenticatedDestinationInspection(rawPreCasProof);
  if (
    !preCasProof ||
    preCasProof.status !== "present-exact" ||
    JSON.stringify(preCasProof) !== JSON.stringify(proof)
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  // No external owner/clock callback is allowed between this final exact
  // destination evidence and the lineage CAS. Abort is a local read only.
  if (input.signal.aborted) {
    return { status: "failed", reason: "aborted" };
  }
  const rebound = replaceAuthorityStrict(anonymousLineage, nextLineage);
  if (
    rebound.status === "replaced" &&
    rebound.draft.kind === "specialist-location-gateway-navigation-lineage"
  ) {
    return { status: "bound", lineage: rebound.draft };
  }
  if (ambiguous(rebound.status)) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  return { status: "failed", reason: "owner-drift" };
}

// Retained only as a local compatibility proof while the paired saga above
// owns every public post-draft auth transition. It cannot be imported by UI.
void bindSpecialistLocationGatewayCommittedLineageAfterAuthStrict;

export type BindSpecialistLocationGatewayCommittedDestinationAfterAuthResult =
  | {
      status: "bound";
      intent: SpecialistLocationGatewayIntentRecordV1;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    }
  | {
      status: "failed";
      reason:
        | "aborted"
        | "already-owned"
        | "destination-mismatch"
        | "destination-missing"
        | "destination-unavailable"
        | "invalid-account"
        | "invalid-input"
        | "owner-drift"
        | "provider-unavailable"
        | "selection-drift";
    }
  | {
      status: "recovery-required";
      reason: "ambiguous-persistence" | "compensation-failed";
    };

async function inspectPostDraftDestinationExact(
  inspector: SpecialistLocationGatewayPostDraftDestinationInspector,
) {
  let raw: unknown;
  try {
    raw = await inspector.inspectExact();
  } catch {
    return null;
  }
  return snapshotPostDraftDestinationInspection(raw);
}

async function bindPostDraftDestinationOwnerExact(input: {
  binding: SpecialistLocationGatewayDurableDestinationOwnerBinding;
  destinationDraftId: string;
  expectedPriorPersistedFingerprint: string;
  expectedAuthenticatedPersistedFingerprint: string;
  authenticatedCustomerAccountId: string;
}) {
  let raw: unknown;
  try {
    raw = await input.binding.bindAuthenticatedOwnerExact({
      destinationDraftId: input.destinationDraftId,
      expectedPriorPersistedFingerprint:
        input.expectedPriorPersistedFingerprint,
      expectedAuthenticatedPersistedFingerprint:
        input.expectedAuthenticatedPersistedFingerprint,
      authenticatedCustomerAccountId:
        input.authenticatedCustomerAccountId,
    });
  } catch {
    return null;
  }
  return snapshotDestinationOwnerMutationInspection(raw);
}

async function inspectPostDraftAuthenticatedDestinationExact(
  binding: SpecialistLocationGatewayDurableDestinationOwnerBinding,
) {
  let raw: unknown;
  try {
    raw = await binding.inspectAuthenticatedExact();
  } catch {
    return null;
  }
  return snapshotAuthenticatedDestinationInspection(raw);
}

function authenticatedDestinationProofMatches(input: {
  proof: SpecialistLocationGatewayAuthenticatedDestinationProof;
  destinationDraftId: string;
  priorPersistedFingerprint: string;
  authenticatedPersistedFingerprint: string;
  authenticatedCustomerAccountId: string;
}) {
  return (
    input.proof.destinationDraftId === input.destinationDraftId &&
    input.proof.priorPersistedFingerprint ===
      input.priorPersistedFingerprint &&
    input.proof.authenticatedPersistedFingerprint ===
      input.authenticatedPersistedFingerprint &&
    input.proof.authenticatedCustomerAccountId ===
      input.authenticatedCustomerAccountId
  );
}

async function inspectCommittedAuthDestinationExact(input: {
  anonymousLineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  destinationInspector: SpecialistLocationGatewayPostDraftDestinationInspector;
  destinationOwnerBinding:
    | SpecialistLocationGatewayDurableDestinationOwnerBinding
    | undefined;
  authenticatedDestinationProof:
    | SpecialistLocationGatewayAuthenticatedDestinationProof
    | null;
}) {
  if (input.destinationOwnerBinding) {
    const proof = await inspectPostDraftAuthenticatedDestinationExact(
      input.destinationOwnerBinding,
    );
    if (!proof || proof.status === "unreadable") return "ambiguous" as const;
    return proof.status === "present-exact" &&
      input.authenticatedDestinationProof &&
      authenticatedDestinationProofMatches({
        proof,
        destinationDraftId:
          input.authenticatedDestinationProof.destinationDraftId,
        priorPersistedFingerprint:
          input.authenticatedDestinationProof.priorPersistedFingerprint,
        authenticatedPersistedFingerprint:
          input.authenticatedDestinationProof
            .authenticatedPersistedFingerprint,
        authenticatedCustomerAccountId:
          input.authenticatedDestinationProof
            .authenticatedCustomerAccountId,
      })
      ? ("exact" as const)
      : ("mismatch" as const);
  }
  const proof = await inspectPostDraftDestinationExact(
    input.destinationInspector,
  );
  if (!proof || proof.status === "unreadable") return "ambiguous" as const;
  return proof.status === "present-exact" &&
    proof.destinationDraftId === input.anonymousLineage.destinationDraftId &&
    proof.persistedFingerprint ===
      input.anonymousLineage.destinationDraftFingerprint
    ? ("exact" as const)
    : ("mismatch" as const);
}

async function restorePostDraftDestinationOwnerExact(input: {
  binding: SpecialistLocationGatewayDurableDestinationOwnerBinding;
  inspector: SpecialistLocationGatewayPostDraftDestinationInspector;
  proof: SpecialistLocationGatewayAuthenticatedDestinationProof;
}) {
  let raw: unknown;
  try {
    raw = await input.binding.restoreAnonymousOwnerExact(input.proof);
  } catch {
    return false;
  }
  const restored = snapshotDestinationOwnerRestorationInspection(raw);
  if (
    !restored ||
    (restored.status !== "restored" &&
      restored.status !== "present-exact") ||
    !authenticatedDestinationProofMatches({
      proof: restored,
      destinationDraftId: input.proof.destinationDraftId,
      priorPersistedFingerprint: input.proof.priorPersistedFingerprint,
      authenticatedPersistedFingerprint:
        input.proof.authenticatedPersistedFingerprint,
      authenticatedCustomerAccountId:
        input.proof.authenticatedCustomerAccountId,
    })
  ) {
    return false;
  }
  const anonymousProof = await inspectPostDraftDestinationExact(
    input.inspector,
  );
  return Boolean(
    anonymousProof?.status === "present-exact" &&
      anonymousProof.destinationDraftId === input.proof.destinationDraftId &&
      anonymousProof.persistedFingerprint ===
        input.proof.priorPersistedFingerprint,
  );
}

function createCommittedAuthRebindClaim(input: {
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  authenticatedIntent: SpecialistLocationGatewayIntentRecordV1;
  authenticatedDestinationDraftFingerprint: string;
  destinationOwnerBindingMode: "companion-only" | "durable-owner";
  authAttemptId: string;
  claimedAtEpochMs: number;
}) {
  if (
    !isSpecialistLocationGatewayIdentifier(input.authAttemptId) ||
    input.lineage.customerAccountId !== null ||
    input.authenticatedIntent.customerAccountId === null ||
    input.authenticatedIntent.draftId !== input.lineage.intentDraftId ||
    input.authenticatedIntent.generation !== input.lineage.generation + 1 ||
    !isSpecialistLocationGatewayFingerprint(
      input.authenticatedDestinationDraftFingerprint,
    ) ||
    (input.destinationOwnerBindingMode === "companion-only") !==
      (input.authenticatedDestinationDraftFingerprint ===
        input.lineage.destinationDraftFingerprint) ||
    !isSpecialistLocationGatewayRecordFresh(
      input.lineage,
      input.claimedAtEpochMs,
    )
  ) {
    return null;
  }
  const body = navigationBase(input.lineage);
  const claim: SpecialistLocationGatewayCommittedAuthRebindClaimV1 = {
    ...body,
    kind: "specialist-location-gateway-committed-auth-rebind-claim",
    mutationRevision: 6,
    resolutionId: input.lineage.resolutionId,
    lineageAttemptId: input.lineage.attemptId,
    authAttemptId: input.authAttemptId,
    destinationKind: input.lineage.destinationKind,
    destinationDraftId: input.lineage.destinationDraftId,
    priorDestinationDraftFingerprint:
      input.lineage.destinationDraftFingerprint,
    authenticatedDestinationDraftFingerprint:
      input.authenticatedDestinationDraftFingerprint,
    destinationOwnerBindingMode: input.destinationOwnerBindingMode,
    authenticatedCustomerAccountId:
      input.authenticatedIntent.customerAccountId,
    targetGeneration: input.authenticatedIntent.generation,
    targetCreatedAtEpochMs: input.authenticatedIntent.createdAtEpochMs,
    targetExpiresAtEpochMs: input.authenticatedIntent.expiresAtEpochMs,
    lineageCommittedAtEpochMs: input.lineage.committedAtEpochMs,
    claimedAtEpochMs: input.claimedAtEpochMs,
    ownerKey: createSpecialistLocationGatewayNavigationOwnerKey({
      phase: "committed-auth-rebind",
      ...body,
      resolutionId: input.lineage.resolutionId,
      lineageAttemptId: input.lineage.attemptId,
      authAttemptId: input.authAttemptId,
      destinationKind: input.lineage.destinationKind,
      destinationDraftId: input.lineage.destinationDraftId,
      priorDestinationDraftFingerprint:
        input.lineage.destinationDraftFingerprint,
      authenticatedDestinationDraftFingerprint:
        input.authenticatedDestinationDraftFingerprint,
      destinationOwnerBindingMode: input.destinationOwnerBindingMode,
      authenticatedCustomerAccountId:
        input.authenticatedIntent.customerAccountId,
      targetGeneration: input.authenticatedIntent.generation,
      targetCreatedAtEpochMs: input.authenticatedIntent.createdAtEpochMs,
      targetExpiresAtEpochMs: input.authenticatedIntent.expiresAtEpochMs,
      lineageCommittedAtEpochMs: input.lineage.committedAtEpochMs,
      claimedAtEpochMs: input.claimedAtEpochMs,
    }),
  };
  const validation = validateSpecialistLocationGatewayNavigationAuthority(
    claim,
  );
  return validation.valid &&
    validation.draft.kind ===
      "specialist-location-gateway-committed-auth-rebind-claim"
    ? validation.draft
    : null;
}

async function compensateCommittedAuthRebind(input: {
  claim: SpecialistLocationGatewayCommittedAuthRebindClaimV1;
  anonymousLineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  anonymousIntent: SpecialistLocationGatewayIntentRecordV1;
  authenticatedIntent: SpecialistLocationGatewayIntentRecordV1;
  destinationInspector: SpecialistLocationGatewayPostDraftDestinationInspector;
  destinationOwnerBinding:
    | SpecialistLocationGatewayDurableDestinationOwnerBinding
    | undefined;
  destinationBindingProvenance: "bound" | "none" | "present-exact";
  authenticatedDestinationProof:
    | SpecialistLocationGatewayAuthenticatedDestinationProof
    | null;
}) {
  if (input.destinationBindingProvenance === "present-exact") {
    quarantineCustomerOwnedSessionStorage();
    return false;
  }
  if (input.destinationBindingProvenance === "bound") {
    if (
      !input.destinationOwnerBinding ||
      !input.authenticatedDestinationProof ||
      !(await restorePostDraftDestinationOwnerExact({
        binding: input.destinationOwnerBinding,
        inspector: input.destinationInspector,
        proof: input.authenticatedDestinationProof,
      }))
    ) {
      quarantineCustomerOwnedSessionStorage();
      return false;
    }
  }
  const intentRollback =
    compareAndSwapSpecialistLocationGatewayIntentStrict({
      expected: input.authenticatedIntent,
      next: input.anonymousIntent,
    });
  if (intentRollback.status !== "replaced") {
    quarantineCustomerOwnedSessionStorage();
    return false;
  }
  const authorityRollback = replaceAuthorityStrict(
    input.claim,
    input.anonymousLineage,
  );
  if (authorityRollback.status !== "replaced") {
    quarantineCustomerOwnedSessionStorage();
    return false;
  }
  return true;
}

async function failAfterCommittedAuthClaim(input: {
  claim: SpecialistLocationGatewayCommittedAuthRebindClaimV1;
  anonymousLineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  anonymousIntent: SpecialistLocationGatewayIntentRecordV1;
  authenticatedIntent: SpecialistLocationGatewayIntentRecordV1;
  destinationInspector: SpecialistLocationGatewayPostDraftDestinationInspector;
  destinationOwnerBinding:
    | SpecialistLocationGatewayDurableDestinationOwnerBinding
    | undefined;
  destinationBindingProvenance: "bound" | "none" | "present-exact";
  authenticatedDestinationProof:
    | SpecialistLocationGatewayAuthenticatedDestinationProof
    | null;
  reason:
    | "aborted"
    | "destination-mismatch"
    | "destination-missing"
    | "destination-unavailable"
    | "owner-drift"
    | "provider-unavailable"
    | "selection-drift";
  ambiguous?: boolean;
}): Promise<BindSpecialistLocationGatewayCommittedDestinationAfterAuthResult> {
  const compensated = await compensateCommittedAuthRebind(input);
  if (!compensated) {
    return { status: "recovery-required", reason: "compensation-failed" };
  }
  if (input.ambiguous) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  return { status: "failed", reason: input.reason };
}

/**
 * Single-owner post-draft auth saga for Individual Appointment V1 and Package
 * V1. The lineage -> claim CAS serializes against Back/Close. The destination
 * may either remain immutable (companion-only) or be rebound by an injected
 * exact durable-owner transaction; clients never compose an intent-only bind.
 */
export async function bindSpecialistLocationGatewayCommittedDestinationAfterAuthStrict(
  input: {
    anonymousIntent: SpecialistLocationGatewayIntentRecordV1;
    anonymousLineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    authenticatedCustomerAccountId: string;
    authAttemptId: string;
    expectedAuthenticatedDestinationFingerprint: string;
    provider: SpecialistLocationGatewayProviderV1;
    destinationInspector: SpecialistLocationGatewayPostDraftDestinationInspector;
    destinationOwnerBinding?: SpecialistLocationGatewayDurableDestinationOwnerBinding;
    signal: AbortSignal;
    now: () => number;
    isCurrentOwner: () => boolean;
  },
): Promise<BindSpecialistLocationGatewayCommittedDestinationAfterAuthResult> {
  let intentValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  let lineageValidation: ReturnType<
    typeof validateSpecialistLocationGatewayNavigationAuthority
  >;
  try {
    intentValidation = validateSpecialistLocationGatewayIntentRecord(
      input.anonymousIntent,
    );
    lineageValidation = validateSpecialistLocationGatewayNavigationAuthority(
      input.anonymousLineage,
    );
  } catch {
    return { status: "failed", reason: "invalid-input" };
  }
  if (
    !intentValidation.valid ||
    !lineageValidation.valid ||
    lineageValidation.draft.kind !==
      "specialist-location-gateway-navigation-lineage" ||
    !isSpecialistLocationGatewayIdentifier(
      input.authenticatedCustomerAccountId,
    ) ||
    !isSpecialistLocationGatewayIdentifier(input.authAttemptId) ||
    !isSpecialistLocationGatewayFingerprint(
      input.expectedAuthenticatedDestinationFingerprint,
    )
  ) {
    return {
      status: "failed",
      reason: isSpecialistLocationGatewayIdentifier(
        input.authenticatedCustomerAccountId,
      )
        ? "invalid-input"
        : "invalid-account",
    };
  }
  const anonymousIntent = intentValidation.draft;
  const anonymousLineage = lineageValidation.draft;
  if (
    anonymousIntent.customerAccountId !== null ||
    anonymousLineage.customerAccountId !== null
  ) {
    return { status: "failed", reason: "already-owned" };
  }
  const destinationOwnerBindingMode = input.destinationOwnerBinding
    ? "durable-owner"
    : "companion-only";
  if (
    (anonymousLineage.destinationKind === "group" &&
      destinationOwnerBindingMode !== "durable-owner") ||
    anonymousIntent.generation >= Number.MAX_SAFE_INTEGER ||
    !resolutionMatchesIntentRecord(anonymousLineage, anonymousIntent) ||
    (destinationOwnerBindingMode === "companion-only") !==
      (input.expectedAuthenticatedDestinationFingerprint ===
        anonymousLineage.destinationDraftFingerprint) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }

  let startedAtEpochMs: number;
  try {
    startedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "provider-unavailable" };
  }
  if (
    safeAuthorityEpoch(startedAtEpochMs) === null ||
    !isSpecialistLocationGatewayRecordFresh(
      anonymousLineage,
      startedAtEpochMs,
    ) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  let authenticatedIntent: SpecialistLocationGatewayIntentRecordV1 | null =
    null;
  try {
    const candidate = createSpecialistLocationGatewayIntentRecord({
      draftId: anonymousIntent.draftId,
      gatewayProviderId: anonymousIntent.gatewayProviderId,
      gatewayProviderRevision: anonymousIntent.gatewayProviderRevision,
      customerAccountId: input.authenticatedCustomerAccountId,
      locale: anonymousIntent.locale,
      generation: anonymousIntent.generation + 1,
      createdAtEpochMs: startedAtEpochMs,
      // The committed destination may carry a shorter selected-row/provider
      // lease than the original pre-draft intent. Auth cannot widen that
      // authority: the rebound intent expires at the earliest durable bound.
      expiresAtEpochMs: Math.min(
        anonymousIntent.expiresAtEpochMs,
        anonymousLineage.expiresAtEpochMs,
      ),
      sourceAction: anonymousIntent.sourceAction,
      source: anonymousIntent.source,
      intent: anonymousIntent.intent,
    });
    const validation = validateSpecialistLocationGatewayIntentRecord(candidate);
    authenticatedIntent = validation.valid ? validation.draft : null;
  } catch {
    authenticatedIntent = null;
  }
  if (!authenticatedIntent) {
    return { status: "failed", reason: "invalid-input" };
  }

  const persistedIntentBefore = readSpecialistLocationGatewayIntentStrict({
    handoffId: anonymousIntent.draftId,
    expectedCustomerAccountId: null,
    expectedLocale: anonymousIntent.locale,
    expectedSourceLocationId: anonymousIntent.source.sourceLocationId,
    expectedSourceUserLocationId:
      anonymousIntent.source.sourceUserLocationId,
    nowEpochMs: startedAtEpochMs,
  });
  const persistedLineageBefore = inspectAuthorityStrict(
    anonymousLineage.draftId,
  );
  if (
    persistedIntentBefore.status !== "ready" ||
    JSON.stringify(persistedIntentBefore.intent) !==
      JSON.stringify(anonymousIntent) ||
    persistedLineageBefore.status !== "present" ||
    JSON.stringify(persistedLineageBefore.draft) !==
      JSON.stringify(anonymousLineage)
  ) {
    if (
      persistedIntentBefore.status === "unavailable" &&
      persistedIntentBefore.reason === "unreadable" ||
      persistedLineageBefore.status === "unreadable"
    ) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }

  const resolvedBefore = await resolveSpecialistLocationGateway({
    intent: authenticatedIntent,
    provider: input.provider,
    signal: input.signal,
    now: input.now,
    isCurrentHandoff: (candidate) =>
      candidate.ownerKey === authenticatedIntent?.ownerKey &&
      operationIsCurrent(input.signal, input.isCurrentOwner),
  });
  if (
    resolvedBefore.status !== "ready" ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "provider-unavailable",
    };
  }
  const initialDestination = await inspectPostDraftDestinationExact(
    input.destinationInspector,
  );
  if (!initialDestination || initialDestination.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (initialDestination.status === "absent") {
    return { status: "failed", reason: "destination-missing" };
  }
  if (initialDestination.status === "failed") {
    return { status: "failed", reason: "destination-unavailable" };
  }
  if (
    initialDestination.destinationDraftId !==
      anonymousLineage.destinationDraftId ||
    initialDestination.persistedFingerprint !==
      anonymousLineage.destinationDraftFingerprint
  ) {
    return { status: "failed", reason: "destination-mismatch" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }

  let claimedAtEpochMs: number;
  try {
    claimedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "provider-unavailable" };
  }
  if (
    safeAuthorityEpoch(claimedAtEpochMs) === null ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const rowsBefore = resolvedBefore.publication.rows.filter(
    (row) =>
      row.locationId === anonymousLineage.selection.selectedLocationId &&
      row.userLocationId ===
        anonymousLineage.selection.selectedUserLocationId,
  );
  const authenticatedResolutionBefore =
    rowsBefore.length === 1
      ? createSpecialistLocationGatewayPreDraftResolution({
          resolutionId: anonymousLineage.resolutionId,
          intentRecord: authenticatedIntent,
          publication: resolvedBefore.publication,
          selectedRow: rowsBefore[0]!,
          nowEpochMs: claimedAtEpochMs,
        })
      : null;
  if (
    !authenticatedResolutionBefore ||
    JSON.stringify(authenticatedResolutionBefore.selection) !==
      JSON.stringify(anonymousLineage.selection)
  ) {
    return { status: "failed", reason: "selection-drift" };
  }
  const claim = createCommittedAuthRebindClaim({
    lineage: anonymousLineage,
    authenticatedIntent,
    authenticatedDestinationDraftFingerprint:
      input.expectedAuthenticatedDestinationFingerprint,
    destinationOwnerBindingMode,
    authAttemptId: input.authAttemptId,
    claimedAtEpochMs,
  });
  if (!claim) return { status: "failed", reason: "invalid-input" };

  const intentProofBeforeClaim = readSpecialistLocationGatewayIntentStrict({
    handoffId: anonymousIntent.draftId,
    expectedCustomerAccountId: null,
    expectedLocale: anonymousIntent.locale,
    expectedSourceLocationId: anonymousIntent.source.sourceLocationId,
    expectedSourceUserLocationId:
      anonymousIntent.source.sourceUserLocationId,
    nowEpochMs: claimedAtEpochMs,
  });
  const lineageProofBeforeClaim = inspectAuthorityStrict(
    anonymousLineage.draftId,
  );
  if (
    intentProofBeforeClaim.status !== "ready" ||
    JSON.stringify(intentProofBeforeClaim.intent) !==
      JSON.stringify(anonymousIntent) ||
    lineageProofBeforeClaim.status !== "present" ||
    JSON.stringify(lineageProofBeforeClaim.draft) !==
      JSON.stringify(anonymousLineage) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    if (lineageProofBeforeClaim.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const claimed = replaceAuthorityStrict(anonymousLineage, claim);
  if (
    claimed.status !== "replaced" ||
    claimed.draft.kind !==
      "specialist-location-gateway-committed-auth-rebind-claim"
  ) {
    if (ambiguous(claimed.status)) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }
  const persistedClaim = claimed.draft;
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    const restored = replaceAuthorityStrict(persistedClaim, anonymousLineage);
    if (restored.status !== "replaced") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "compensation-failed" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }

  const intentTransition =
    compareAndSwapSpecialistLocationGatewayIntentStrict({
      expected: anonymousIntent,
      next: authenticatedIntent,
    });
  if (intentTransition.status !== "replaced") {
    if (intentTransition.status === "recovery-required") {
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    const restored = replaceAuthorityStrict(persistedClaim, anonymousLineage);
    if (restored.status !== "replaced") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "compensation-failed" };
    }
    return { status: "failed", reason: "owner-drift" };
  }
  const persistedAuthenticatedIntent = intentTransition.intent;
  let destinationBindingProvenance:
    | "bound"
    | "none"
    | "present-exact" = "none";
  let authenticatedDestinationProof:
    | SpecialistLocationGatewayAuthenticatedDestinationProof
    | null = null;

  if (input.destinationOwnerBinding) {
    const bound = await bindPostDraftDestinationOwnerExact({
      binding: input.destinationOwnerBinding,
      destinationDraftId: anonymousLineage.destinationDraftId,
      expectedPriorPersistedFingerprint:
        anonymousLineage.destinationDraftFingerprint,
      expectedAuthenticatedPersistedFingerprint:
        input.expectedAuthenticatedDestinationFingerprint,
      authenticatedCustomerAccountId:
        input.authenticatedCustomerAccountId,
    });
    if (!bound || bound.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    if (bound.status === "absent" || bound.status === "failed") {
      return failAfterCommittedAuthClaim({
        claim: persistedClaim,
        anonymousLineage,
        anonymousIntent,
        authenticatedIntent: persistedAuthenticatedIntent,
        destinationInspector: input.destinationInspector,
        destinationOwnerBinding: input.destinationOwnerBinding,
        destinationBindingProvenance,
        authenticatedDestinationProof,
        reason:
          bound.status === "absent"
            ? "destination-missing"
            : "destination-unavailable",
      });
    }
    if (
      !authenticatedDestinationProofMatches({
        proof: bound,
        destinationDraftId: anonymousLineage.destinationDraftId,
        priorPersistedFingerprint:
          anonymousLineage.destinationDraftFingerprint,
        authenticatedPersistedFingerprint:
          input.expectedAuthenticatedDestinationFingerprint,
        authenticatedCustomerAccountId:
          input.authenticatedCustomerAccountId,
      })
    ) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    destinationBindingProvenance = bound.status;
    authenticatedDestinationProof = {
      destinationDraftId: bound.destinationDraftId,
      priorPersistedFingerprint: bound.priorPersistedFingerprint,
      authenticatedPersistedFingerprint:
        bound.authenticatedPersistedFingerprint,
      authenticatedCustomerAccountId:
        bound.authenticatedCustomerAccountId,
    };
  } else {
    const unchanged = await inspectPostDraftDestinationExact(
      input.destinationInspector,
    );
    if (
      !unchanged ||
      unchanged.status !== "present-exact" ||
      unchanged.destinationDraftId !== anonymousLineage.destinationDraftId ||
      unchanged.persistedFingerprint !==
        anonymousLineage.destinationDraftFingerprint
    ) {
      return failAfterCommittedAuthClaim({
        claim: persistedClaim,
        anonymousLineage,
        anonymousIntent,
        authenticatedIntent: persistedAuthenticatedIntent,
        destinationInspector: input.destinationInspector,
        destinationOwnerBinding: undefined,
        destinationBindingProvenance,
        authenticatedDestinationProof,
        reason:
          unchanged?.status === "absent"
            ? "destination-missing"
            : unchanged?.status === "failed"
              ? "destination-unavailable"
              : "destination-mismatch",
        ambiguous: !unchanged || unchanged.status === "unreadable",
      });
    }
  }

  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    });
  }

  const resolvedAfter = await resolveSpecialistLocationGateway({
    intent: persistedAuthenticatedIntent,
    provider: input.provider,
    signal: input.signal,
    now: input.now,
    isCurrentHandoff: (candidate) =>
      candidate.ownerKey === persistedAuthenticatedIntent.ownerKey &&
      operationIsCurrent(input.signal, input.isCurrentOwner),
  });
  if (
    resolvedAfter.status !== "ready" ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: input.signal.aborted ? "aborted" : "provider-unavailable",
    });
  }
  let completedAtEpochMs: number;
  try {
    completedAtEpochMs = input.now();
  } catch {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: "provider-unavailable",
    });
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    });
  }
  const rowsAfter = resolvedAfter.publication.rows.filter(
    (row) =>
      row.locationId === anonymousLineage.selection.selectedLocationId &&
      row.userLocationId ===
        anonymousLineage.selection.selectedUserLocationId,
  );
  const authenticatedResolution =
    rowsAfter.length === 1
      ? createSpecialistLocationGatewayPreDraftResolution({
          resolutionId: anonymousLineage.resolutionId,
          intentRecord: persistedAuthenticatedIntent,
          publication: resolvedAfter.publication,
          selectedRow: rowsAfter[0]!,
          nowEpochMs: completedAtEpochMs,
        })
      : null;
  if (
    !authenticatedResolution ||
    JSON.stringify(authenticatedResolution.selection) !==
      JSON.stringify(anonymousLineage.selection)
  ) {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: "selection-drift",
    });
  }
  const nextLineage = createAuthenticatedCommittedLineage({
    anonymousLineage,
    authenticatedResolution,
    authenticatedDestinationFingerprint:
      input.expectedAuthenticatedDestinationFingerprint,
    committedAtEpochMs: completedAtEpochMs,
  });
  if (!nextLineage) {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: "selection-drift",
    });
  }

  const intentProof = readSpecialistLocationGatewayIntentStrict({
    handoffId: persistedAuthenticatedIntent.draftId,
    expectedCustomerAccountId:
      persistedAuthenticatedIntent.customerAccountId,
    expectedLocale: persistedAuthenticatedIntent.locale,
    expectedSourceLocationId:
      persistedAuthenticatedIntent.source.sourceLocationId,
    expectedSourceUserLocationId:
      persistedAuthenticatedIntent.source.sourceUserLocationId,
    nowEpochMs: completedAtEpochMs,
  });
  const claimProof = inspectAuthorityStrict(persistedClaim.draftId);
  if (
    intentProof.status !== "ready" ||
    JSON.stringify(intentProof.intent) !==
      JSON.stringify(persistedAuthenticatedIntent) ||
    claimProof.status !== "present" ||
    JSON.stringify(claimProof.draft) !== JSON.stringify(persistedClaim) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: input.signal.aborted ? "aborted" : "owner-drift",
      ambiguous:
        claimProof.status === "unreadable" ||
        (intentProof.status === "unavailable" &&
          intentProof.reason === "unreadable"),
    });
  }

  const firstFinalProof = await inspectCommittedAuthDestinationExact({
    anonymousLineage,
    destinationInspector: input.destinationInspector,
    destinationOwnerBinding: input.destinationOwnerBinding,
    authenticatedDestinationProof,
  });
  if (firstFinalProof !== "exact") {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: "destination-mismatch",
      ambiguous: firstFinalProof === "ambiguous",
    });
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    });
  }
  const preCasDestinationProof =
    await inspectCommittedAuthDestinationExact({
      anonymousLineage,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      authenticatedDestinationProof,
    });
  if (preCasDestinationProof !== "exact") {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: "destination-mismatch",
      ambiguous: preCasDestinationProof === "ambiguous",
    });
  }
  // The second destination proof can itself invalidate the mounted owner.
  // Re-read both owner and signal after that external callback; the helper
  // also checks the signal again after the owner callback returns.
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return failAfterCommittedAuthClaim({
      claim: persistedClaim,
      anonymousLineage,
      anonymousIntent,
      authenticatedIntent: persistedAuthenticatedIntent,
      destinationInspector: input.destinationInspector,
      destinationOwnerBinding: input.destinationOwnerBinding,
      destinationBindingProvenance,
      authenticatedDestinationProof,
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    });
  }
  const completed = replaceAuthorityStrict(persistedClaim, nextLineage);
  if (
    completed.status === "replaced" &&
    completed.draft.kind ===
      "specialist-location-gateway-navigation-lineage"
  ) {
    return {
      status: "bound",
      intent: persistedAuthenticatedIntent,
      lineage: completed.draft,
    };
  }
  if (ambiguous(completed.status)) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  return failAfterCommittedAuthClaim({
    claim: persistedClaim,
    anonymousLineage,
    anonymousIntent,
    authenticatedIntent: persistedAuthenticatedIntent,
    destinationInspector: input.destinationInspector,
    destinationOwnerBinding: input.destinationOwnerBinding,
    destinationBindingProvenance,
    authenticatedDestinationProof,
    reason: "owner-drift",
  });
}

export type ReturnSpecialistLocationGatewayDestinationToPreDraftResult =
  | {
      status: "returned";
      intent: SpecialistLocationGatewayIntentRecordV1;
      resolution: SpecialistLocationGatewayPreDraftResolutionV1;
    }
  | {
      status: "failed";
      reason:
        | "aborted"
        | "terminal-or-ambiguous-evidence"
        | "destination-missing"
        | "destination-unavailable"
        | "invalid-input"
        | "owner-drift"
        | "provider-unavailable"
        | "selection-drift";
    }
  | {
      status: "recovery-required";
      reason: "ambiguous-persistence" | "compensation-failed";
    };

function lifecycleInspectionFailure(
  inspection: SpecialistLocationGatewayMutableDestinationInspection | null,
):
  | {
      status: "failed";
      reason:
        | "terminal-or-ambiguous-evidence"
        | "destination-missing"
        | "destination-unavailable";
    }
  | { status: "recovery-required"; reason: "ambiguous-persistence" } {
  if (
    !inspection ||
    inspection.status === "ambiguous" ||
    inspection.status === "unreadable"
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (inspection.status === "terminal-or-ambiguous-blocked") {
    return { status: "failed", reason: "terminal-or-ambiguous-evidence" };
  }
  if (inspection.status === "absent") {
    return { status: "failed", reason: "destination-missing" };
  }
  return { status: "failed", reason: "destination-unavailable" };
}

/**
 * Exact downstream Back transaction. No destination bytes are removed until
 * fresh provider truth has rebuilt the same selected-B resolution and the
 * persisted lineage has been serialized into a lifecycle claim.
 */
export async function returnSpecialistLocationGatewayDestinationToPreDraftStrict(
  input: {
    lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    intent: SpecialistLocationGatewayIntentRecordV1;
    lifecycleAttemptId: string;
    provider: SpecialistLocationGatewayProviderV1;
    destinationLifecycle: SpecialistLocationGatewayDestinationLifecycle;
    signal: AbortSignal;
    now: () => number;
    isCurrentOwner: () => boolean;
  },
): Promise<ReturnSpecialistLocationGatewayDestinationToPreDraftResult> {
  let lineageValidation: ReturnType<
    typeof validateSpecialistLocationGatewayNavigationAuthority
  >;
  let intentValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    lineageValidation = validateSpecialistLocationGatewayNavigationAuthority(
      input.lineage,
    );
    intentValidation = validateSpecialistLocationGatewayIntentRecord(
      input.intent,
    );
  } catch {
    return { status: "failed", reason: "invalid-input" };
  }
  if (
    !lineageValidation.valid ||
    lineageValidation.draft.kind !==
      "specialist-location-gateway-navigation-lineage" ||
    !intentValidation.valid ||
    !isSpecialistLocationGatewayIdentifier(input.lifecycleAttemptId)
  ) {
    return { status: "failed", reason: "invalid-input" };
  }
  const lineage = lineageValidation.draft;
  const intent = intentValidation.draft;
  if (
    !resolutionMatchesIntentRecord(lineage, intent) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  let startedAtEpochMs: number;
  try {
    startedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "provider-unavailable" };
  }
  if (
    safeAuthorityEpoch(startedAtEpochMs) === null ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  const persistedIntentBefore =
    inspectSpecialistLocationGatewayIntentExactPersisted(intent);
  const persistedLineageBefore = inspectAuthorityStrict(lineage.draftId);
  if (
    persistedIntentBefore.status !== "present-exact" ||
    persistedLineageBefore.status !== "present" ||
    JSON.stringify(persistedLineageBefore.draft) !== JSON.stringify(lineage)
  ) {
    if (persistedLineageBefore.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }

  let resolutionIntent = intent;
  const refreshesExpiredIntent =
    !isSpecialistLocationGatewayRecordFresh(intent, startedAtEpochMs) ||
    !isSpecialistLocationGatewayRecordFresh(lineage, startedAtEpochMs);
  if (refreshesExpiredIntent) {
    let providerIdentity: ReturnType<
      SpecialistLocationGatewayProviderV1["getIdentity"]
    >;
    try {
      providerIdentity = input.provider.getIdentity();
    } catch {
      return { status: "failed", reason: "provider-unavailable" };
    }
    if (
      !providerIdentity ||
      !operationIsCurrent(input.signal, input.isCurrentOwner)
    ) {
      return {
        status: "failed",
        reason: input.signal.aborted ? "aborted" : "provider-unavailable",
      };
    }
    const refreshedIntent = createRefreshedDestinationLifecycleIntent({
      intent,
      gatewayProviderId: providerIdentity.providerId,
      gatewayProviderRevision: providerIdentity.providerRevision,
      refreshedAtEpochMs: startedAtEpochMs,
    });
    if (!refreshedIntent) {
      return { status: "failed", reason: "provider-unavailable" };
    }
    resolutionIntent = refreshedIntent;
  }

  const resolved = await resolveSpecialistLocationGateway({
    intent: resolutionIntent,
    provider: input.provider,
    signal: input.signal,
    now: input.now,
    isCurrentHandoff: (candidate) =>
      candidate.ownerKey === resolutionIntent.ownerKey &&
      operationIsCurrent(input.signal, input.isCurrentOwner),
  });
  if (
    resolved.status !== "ready" ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "provider-unavailable",
    };
  }
  let claimedAtEpochMs: number;
  try {
    claimedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "provider-unavailable" };
  }
  if (
    safeAuthorityEpoch(claimedAtEpochMs) === null ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const selectedRows = resolved.publication.rows.filter(
    (row) =>
      row.locationId === lineage.selection.selectedLocationId &&
      row.userLocationId === lineage.selection.selectedUserLocationId,
  );
  const freshResolution =
    selectedRows.length === 1
      ? createSpecialistLocationGatewayPreDraftResolution({
          resolutionId: lineage.resolutionId,
          intentRecord: resolutionIntent,
          publication: resolved.publication,
          selectedRow: selectedRows[0]!,
          nowEpochMs: claimedAtEpochMs,
        })
      : null;
  if (
    !freshResolution ||
    JSON.stringify(freshResolution.selection) !==
      JSON.stringify(lineage.selection)
  ) {
    return { status: "failed", reason: "selection-drift" };
  }

  const mutable = await inspectMutableDestinationExact(
    input.destinationLifecycle,
  );
  if (!mutable || mutable.status !== "mutable-cleanup-exact") {
    return lifecycleInspectionFailure(mutable);
  }
  if (!mutableProofMatchesLineage(mutable, lineage)) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const intentAfter =
    inspectSpecialistLocationGatewayIntentExactPersisted(intent);
  const lineageAfter = inspectAuthorityStrict(lineage.draftId);
  if (
    intentAfter.status !== "present-exact" ||
    lineageAfter.status !== "present" ||
    JSON.stringify(lineageAfter.draft) !== JSON.stringify(lineage)
  ) {
    return { status: "failed", reason: "owner-drift" };
  }
  const claim = createDestinationLifecycleClaim({
    lineage,
    lifecycleAttemptId: input.lifecycleAttemptId,
    action: "return-to-pre-draft",
    proof: mutable,
    claimedAtEpochMs,
  });
  if (!claim) return { status: "failed", reason: "invalid-input" };
  const claimed = replaceAuthorityStrict(lineage, claim);
  if (
    claimed.status !== "replaced" ||
    claimed.draft.kind !==
      "specialist-location-gateway-destination-lifecycle-claim"
  ) {
    if (ambiguous(claimed.status)) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }
  const persistedClaim = claimed.draft;
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    const restored = replaceAuthorityStrict(persistedClaim, lineage);
    if (restored.status !== "replaced") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }

  const mutableAfterClaim = await inspectMutableDestinationExact(
    input.destinationLifecycle,
  );
  if (
    !mutableAfterClaim ||
    mutableAfterClaim.status !== "mutable-cleanup-exact" ||
    JSON.stringify(mutableAfterClaim) !== JSON.stringify(mutable)
  ) {
    if (
      mutableAfterClaim?.status === "terminal-or-ambiguous-blocked" ||
      mutableAfterClaim?.status === "failed"
    ) {
      const restored = replaceAuthorityStrict(persistedClaim, lineage);
      if (restored.status === "replaced") {
        return lifecycleInspectionFailure(mutableAfterClaim);
      }
    }
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    const restored = replaceAuthorityStrict(persistedClaim, lineage);
    if (restored.status !== "replaced") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const removed = await removeMutableDestinationExact({
    lifecycle: input.destinationLifecycle,
    proof: mutable,
  });
  if (!removed || removed.status !== "removed") {
    if (removed?.status === "failed") {
      const restored = replaceAuthorityStrict(persistedClaim, lineage);
      if (restored.status === "replaced") {
        return { status: "failed", reason: "destination-unavailable" };
      }
    }
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (
    removed.destinationDraftId !== mutable.destinationDraftId ||
    removed.persistedFingerprint !== mutable.persistedFingerprint ||
    removed.mutableCleanupFingerprint !==
      mutable.mutableCleanupFingerprint
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    const compensated = await compensateDestinationLifecycle({
      lifecycle: input.destinationLifecycle,
      proof: mutable,
      claim: persistedClaim,
      lineage,
    });
    return compensated
      ? {
          status: "failed",
          reason: input.signal.aborted ? "aborted" : "owner-drift",
        }
      : { status: "recovery-required", reason: "compensation-failed" };
  }
  const claimProof = inspectAuthorityStrict(persistedClaim.draftId);
  const intentProof =
    inspectSpecialistLocationGatewayIntentExactPersisted(intent);
  if (
    claimProof.status !== "present" ||
    JSON.stringify(claimProof.draft) !== JSON.stringify(persistedClaim) ||
    intentProof.status !== "present-exact" ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    const compensated = await compensateDestinationLifecycle({
      lifecycle: input.destinationLifecycle,
      proof: mutable,
      claim: persistedClaim,
      lineage,
    });
    return compensated
      ? {
          status: "failed",
          reason: input.signal.aborted ? "aborted" : "owner-drift",
        }
      : { status: "recovery-required", reason: "compensation-failed" };
  }
  let persistedResolutionIntent = intent;
  if (refreshesExpiredIntent) {
    const intentTransition =
      compareAndSwapSpecialistLocationGatewayIntentStrict({
        expected: intent,
        next: resolutionIntent,
      });
    if (intentTransition.status !== "replaced") {
      const compensated = await compensateDestinationLifecycle({
        lifecycle: input.destinationLifecycle,
        proof: mutable,
        claim: persistedClaim,
        lineage,
      });
      if (
        intentTransition.status === "recovery-required" ||
        !compensated
      ) {
        quarantineCustomerOwnedSessionStorage();
        return { status: "recovery-required", reason: "compensation-failed" };
      }
      return { status: "failed", reason: "owner-drift" };
    }
    persistedResolutionIntent = intentTransition.intent;
    if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
      const intentRestored =
        compareAndSwapSpecialistLocationGatewayIntentStrict({
          expected: persistedResolutionIntent,
          next: intent,
        });
      const compensated = await compensateDestinationLifecycle({
        lifecycle: input.destinationLifecycle,
        proof: mutable,
        claim: persistedClaim,
        lineage,
      });
      if (intentRestored.status !== "replaced" || !compensated) {
        quarantineCustomerOwnedSessionStorage();
        return { status: "recovery-required", reason: "compensation-failed" };
      }
      return {
        status: "failed",
        reason: input.signal.aborted ? "aborted" : "owner-drift",
      };
    }
  }
  const completed = replaceAuthorityStrict(persistedClaim, freshResolution);
  if (
    completed.status === "replaced" &&
    completed.draft.kind ===
      "specialist-location-gateway-pre-draft-resolution"
  ) {
    return {
      status: "returned",
      intent: persistedResolutionIntent,
      resolution: completed.draft,
    };
  }
  const inspected = inspectAuthorityStrict(freshResolution.draftId);
  if (
    inspected.status === "present" &&
    JSON.stringify(inspected.draft) === JSON.stringify(freshResolution)
  ) {
    return {
      status: "returned",
      intent: persistedResolutionIntent,
      resolution: freshResolution,
    };
  }
  if (!ambiguous(completed.status)) {
    const intentRestored = refreshesExpiredIntent
      ? compareAndSwapSpecialistLocationGatewayIntentStrict({
          expected: persistedResolutionIntent,
          next: intent,
        }).status === "replaced"
      : true;
    const compensated = await compensateDestinationLifecycle({
      lifecycle: input.destinationLifecycle,
      proof: mutable,
      claim: persistedClaim,
      lineage,
    });
    if (intentRestored && compensated) {
      return { status: "failed", reason: "owner-drift" };
    }
  }
  quarantineCustomerOwnedSessionStorage();
  return { status: "recovery-required", reason: "ambiguous-persistence" };
}

export type CloseSpecialistLocationGatewayCommittedDestinationResult =
  | { status: "closed"; returnHref: string }
  | {
      status: "failed";
      reason:
        | "aborted"
        | "terminal-or-ambiguous-evidence"
        | "destination-missing"
        | "destination-unavailable"
        | "invalid-input"
        | "owner-drift";
    }
  | {
      status: "recovery-required";
      reason: "ambiguous-persistence" | "compensation-failed";
    };

/** Exact downstream Close transaction: destination aggregate -> lineage -> intent. */
export async function closeSpecialistLocationGatewayCommittedDestinationStrict(
  input: {
    lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    intent: SpecialistLocationGatewayIntentRecordV1;
    lifecycleAttemptId: string;
    destinationLifecycle: SpecialistLocationGatewayDestinationLifecycle;
    signal: AbortSignal;
    now: () => number;
    isCurrentOwner: () => boolean;
  },
): Promise<CloseSpecialistLocationGatewayCommittedDestinationResult> {
  let lineageValidation: ReturnType<
    typeof validateSpecialistLocationGatewayNavigationAuthority
  >;
  let intentValidation: ReturnType<
    typeof validateSpecialistLocationGatewayIntentRecord
  >;
  try {
    lineageValidation = validateSpecialistLocationGatewayNavigationAuthority(
      input.lineage,
    );
    intentValidation = validateSpecialistLocationGatewayIntentRecord(
      input.intent,
    );
  } catch {
    return { status: "failed", reason: "invalid-input" };
  }
  if (
    !lineageValidation.valid ||
    lineageValidation.draft.kind !==
      "specialist-location-gateway-navigation-lineage" ||
    !intentValidation.valid ||
    !isSpecialistLocationGatewayIdentifier(input.lifecycleAttemptId)
  ) {
    return { status: "failed", reason: "invalid-input" };
  }
  const lineage = lineageValidation.draft;
  const intent = intentValidation.draft;
  if (
    !resolutionMatchesIntentRecord(lineage, intent) ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  let claimedAtEpochMs: number;
  try {
    claimedAtEpochMs = input.now();
  } catch {
    return { status: "failed", reason: "invalid-input" };
  }
  if (
    safeAuthorityEpoch(claimedAtEpochMs) === null ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "invalid-input",
    };
  }
  const persistedIntent =
    inspectSpecialistLocationGatewayIntentExactPersisted(intent);
  const persistedLineage = inspectAuthorityStrict(lineage.draftId);
  if (
    persistedIntent.status !== "present-exact" ||
    persistedLineage.status !== "present" ||
    JSON.stringify(persistedLineage.draft) !== JSON.stringify(lineage)
  ) {
    return { status: "failed", reason: "owner-drift" };
  }
  const mutable = await inspectMutableDestinationExact(
    input.destinationLifecycle,
  );
  if (!mutable || mutable.status !== "mutable-cleanup-exact") {
    const failed = lifecycleInspectionFailure(mutable);
    return failed.status === "recovery-required"
      ? failed
      : { status: "failed", reason: failed.reason };
  }
  if (!mutableProofMatchesLineage(mutable, lineage)) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const claim = createDestinationLifecycleClaim({
    lineage,
    lifecycleAttemptId: input.lifecycleAttemptId,
    action: "close",
    proof: mutable,
    claimedAtEpochMs,
  });
  if (!claim) return { status: "failed", reason: "invalid-input" };
  const claimed = replaceAuthorityStrict(lineage, claim);
  if (
    claimed.status !== "replaced" ||
    claimed.draft.kind !==
      "specialist-location-gateway-destination-lifecycle-claim"
  ) {
    if (ambiguous(claimed.status)) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "failed", reason: "owner-drift" };
  }
  const persistedClaim = claimed.draft;
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    const restored = replaceAuthorityStrict(persistedClaim, lineage);
    if (restored.status !== "replaced") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const mutableAfterClaim = await inspectMutableDestinationExact(
    input.destinationLifecycle,
  );
  if (
    !mutableAfterClaim ||
    mutableAfterClaim.status !== "mutable-cleanup-exact" ||
    JSON.stringify(mutableAfterClaim) !== JSON.stringify(mutable)
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    const restored = replaceAuthorityStrict(persistedClaim, lineage);
    if (restored.status !== "replaced") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return {
      status: "failed",
      reason: input.signal.aborted ? "aborted" : "owner-drift",
    };
  }
  const removedDestination = await removeMutableDestinationExact({
    lifecycle: input.destinationLifecycle,
    proof: mutable,
  });
  if (
    !removedDestination ||
    removedDestination.status !== "removed" ||
    removedDestination.destinationDraftId !== mutable.destinationDraftId ||
    removedDestination.persistedFingerprint !== mutable.persistedFingerprint ||
    removedDestination.mutableCleanupFingerprint !==
      mutable.mutableCleanupFingerprint
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    const compensated = await compensateDestinationLifecycle({
      lifecycle: input.destinationLifecycle,
      proof: mutable,
      claim: persistedClaim,
      lineage,
    });
    return compensated
      ? {
          status: "failed",
          reason: input.signal.aborted ? "aborted" : "owner-drift",
        }
      : { status: "recovery-required", reason: "compensation-failed" };
  }
  const claimProof = inspectAuthorityStrict(persistedClaim.draftId);
  const intentProof =
    inspectSpecialistLocationGatewayIntentExactPersisted(intent);
  if (
    claimProof.status !== "present" ||
    JSON.stringify(claimProof.draft) !== JSON.stringify(persistedClaim) ||
    intentProof.status !== "present-exact" ||
    !operationIsCurrent(input.signal, input.isCurrentOwner)
  ) {
    const compensated = await compensateDestinationLifecycle({
      lifecycle: input.destinationLifecycle,
      proof: mutable,
      claim: persistedClaim,
      lineage,
    });
    return compensated
      ? { status: "failed", reason: "owner-drift" }
      : { status: "recovery-required", reason: "compensation-failed" };
  }
  const removedLineage = removeAuthorityStrict(persistedClaim);
  if (removedLineage.status !== "removed") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  if (!operationIsCurrent(input.signal, input.isCurrentOwner)) {
    const compensated = await compensateAfterLifecycleAuthorityRemoval({
      lifecycle: input.destinationLifecycle,
      proof: mutable,
      lineage,
    });
    return compensated
      ? {
          status: "failed",
          reason: input.signal.aborted ? "aborted" : "owner-drift",
        }
      : { status: "recovery-required", reason: "compensation-failed" };
  }
  const removedIntent = removeSpecialistLocationGatewayIntentStrict(intent);
  if (removedIntent.status !== "removed") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  }
  return { status: "closed", returnHref: intent.source.canonicalReturnHref };
}

/** Exact direct lookup by the destination domain draft ID. */
export function readSpecialistLocationGatewayLineageByDestinationStrict(
  input: {
    destinationDraftId: string;
    expectedDestinationFingerprint: string;
    expectedCustomerAccountId: string | null;
    expectedDestinationKind: SpecialistLocationGatewayDestinationKind;
    expectedSelectedLocationId: string;
    expectedSelectedUserLocationId: string;
    persistedAtEpochMs?: number;
  },
) {
  const persistedAtEpochMs =
    input.persistedAtEpochMs === undefined
      ? undefined
      : safeAuthorityEpoch(input.persistedAtEpochMs);
  if (persistedAtEpochMs === null) {
    return { status: "unreadable" } as const;
  }
  const inspected = inspectAuthoritiesStrict(persistedAtEpochMs);
  if (inspected.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" } as const;
  }
  const matches = inspected.drafts.filter((draft) => {
    switch (draft.kind) {
      case "specialist-location-gateway-navigation-commit-claim":
      case "specialist-location-gateway-navigation-lineage":
      case "specialist-location-gateway-destination-lifecycle-claim":
      case "specialist-location-gateway-committed-auth-rebind-claim":
        return draft.destinationDraftId === input.destinationDraftId;
      default:
        return false;
    }
  });
  if (matches.length === 0) return { status: "absent" } as const;
  if (matches.length !== 1) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" } as const;
  }
  const lineage = matches[0];
  if (!lineage || lineage.kind !== "specialist-location-gateway-navigation-lineage") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" } as const;
  }
  if (
    !isSpecialistLocationGatewayFingerprint(
      input.expectedDestinationFingerprint,
    ) ||
    lineage.destinationDraftFingerprint !==
      input.expectedDestinationFingerprint ||
    lineage.customerAccountId !== input.expectedCustomerAccountId ||
    lineage.destinationKind !== input.expectedDestinationKind ||
    lineage.selection.selectedLocationId !==
      input.expectedSelectedLocationId ||
    lineage.selection.selectedUserLocationId !==
      input.expectedSelectedUserLocationId
  ) {
    return { status: "owner-mismatch" } as const;
  }
  return { status: "present", lineage } as const;
}

export type ReadSpecialistLocationGatewayCommittedDestinationContextResult =
  | {
      status: "ready";
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
      intent: SpecialistLocationGatewayIntentRecordV1;
    }
  | {
      status: "unavailable";
      reason: "absent" | "expired" | "owner-mismatch" | "unreadable";
    };

/**
 * Store-owned committed entry reader. Destination routes know B and the
 * immutable destination proof, but never need source-A route identifiers from
 * a URL: the exact lineage recovers them and the paired intent is then read
 * and compared before either record is returned.
 */
export function readSpecialistLocationGatewayCommittedDestinationContextStrict(
  input: {
    destinationDraftId: string;
    expectedDestinationFingerprint: string;
    expectedCustomerAccountId: string | null;
    expectedDestinationKind: SpecialistLocationGatewayDestinationKind;
    expectedSelectedLocationId: string;
    expectedSelectedUserLocationId: string;
    nowEpochMs?: number;
  },
): ReadSpecialistLocationGatewayCommittedDestinationContextResult {
  try {
    const nowEpochMs = safeAuthorityEpoch(input.nowEpochMs);
    if (nowEpochMs === null) {
      return { status: "unavailable", reason: "unreadable" };
    }
    const lineageRead =
      readSpecialistLocationGatewayLineageByDestinationStrict(input);
    if (lineageRead.status !== "present") {
      return {
        status: "unavailable",
        reason:
          lineageRead.status === "absent"
            ? "absent"
            : lineageRead.status === "owner-mismatch"
              ? "owner-mismatch"
              : "unreadable",
      };
    }
    const lineage = lineageRead.lineage;
    if (!isSpecialistLocationGatewayRecordFresh(lineage, nowEpochMs)) {
      return { status: "unavailable", reason: "expired" };
    }
    const intentRead = readSpecialistLocationGatewayIntentStrict({
      handoffId: lineage.intentDraftId,
      expectedCustomerAccountId: lineage.customerAccountId,
      expectedLocale: lineage.locale,
      expectedSourceLocationId: lineage.source.sourceLocationId,
      expectedSourceUserLocationId: lineage.source.sourceUserLocationId,
      nowEpochMs,
    });
    if (
      intentRead.status !== "ready" ||
      !resolutionMatchesIntentRecord(lineage, intentRead.intent)
    ) {
      if (
        intentRead.status === "unavailable" &&
        intentRead.reason === "expired"
      ) {
        return { status: "unavailable", reason: "expired" };
      }
      quarantineCustomerOwnedSessionStorage();
      return { status: "unavailable", reason: "unreadable" };
    }
    return { status: "ready", lineage, intent: intentRead.intent };
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unavailable", reason: "unreadable" };
  }
}

export type ReadSpecialistLocationGatewayCommittedLifecycleAuthContextResult =
  | {
      status: "ready";
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
      intent: SpecialistLocationGatewayIntentRecordV1;
    }
  | {
      status: "unavailable";
      reason: "absent" | "owner-mismatch" | "unreadable";
    };

function readDurableSpecialistLocationGatewayIntentForLineageStrict(
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1,
) {
  const intents = inspectSpecialistLocationGatewayIntentsStrict();
  if (intents.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" } as const;
  }
  const pairedIntents = intents.drafts.filter(
    (intent) => intent.draftId === lineage.intentDraftId,
  );
  if (
    pairedIntents.length !== 1 ||
    !resolutionMatchesIntentRecord(lineage, pairedIntents[0]!)
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" } as const;
  }
  return { status: "ready", intent: pairedIntents[0]! } as const;
}

/**
 * Durable committed-context reader reserved for lifecycle/auth recovery.
 * The short pre-draft TTL is deliberately irrelevant after a destination has
 * committed, while both versioned stores still enforce their hard retention.
 * This only returns evidence; it performs no provider call or mutation.
 */
export function readSpecialistLocationGatewayCommittedLifecycleAuthContextStrict(
  input: {
    destinationDraftId: string;
    expectedDestinationFingerprint: string;
    expectedCustomerAccountId: string | null;
    expectedDestinationKind: SpecialistLocationGatewayDestinationKind;
    expectedSelectedLocationId: string;
    expectedSelectedUserLocationId: string;
  },
): ReadSpecialistLocationGatewayCommittedLifecycleAuthContextResult {
  try {
    const lineageRead =
      readSpecialistLocationGatewayLineageByDestinationStrict(input);
    if (lineageRead.status !== "present") {
      return {
        status: "unavailable",
        reason:
          lineageRead.status === "absent"
            ? "absent"
            : lineageRead.status === "owner-mismatch"
              ? "owner-mismatch"
              : "unreadable",
      };
    }

    const intentRead =
      readDurableSpecialistLocationGatewayIntentForLineageStrict(
        lineageRead.lineage,
      );
    if (intentRead.status !== "ready") {
      return { status: "unavailable", reason: "unreadable" };
    }
    return {
      status: "ready",
      lineage: lineageRead.lineage,
      intent: intentRead.intent,
    };
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unavailable", reason: "unreadable" };
  }
}

export interface ReadSpecialistLocationGatewayCommittedLifecycleAuthContextByResolutionIdInput {
  resolutionId: string;
  expectedCustomerAccountId?: string | null;
  expectedSourceLocationId?: string;
  expectedSourceUserLocationId?: string;
  expectedSelectedLocationId?: string;
  expectedSelectedUserLocationId?: string;
}

/**
 * Resolution-owned variant for Services recovery. Optional caller evidence is
 * narrowing-only: source A and selected B must be supplied as complete route
 * pairs, and no omitted value is inferred from a URL or provider fallback.
 */
export function readSpecialistLocationGatewayCommittedLifecycleAuthContextByResolutionIdStrict(
  input: ReadSpecialistLocationGatewayCommittedLifecycleAuthContextByResolutionIdInput,
): ReadSpecialistLocationGatewayCommittedLifecycleAuthContextResult {
  try {
    const hasExpectedCustomer = Object.prototype.hasOwnProperty.call(
      input,
      "expectedCustomerAccountId",
    );
    const hasExpectedSourceLocation =
      input.expectedSourceLocationId !== undefined;
    const hasExpectedSourceAssignment =
      input.expectedSourceUserLocationId !== undefined;
    const hasExpectedSelectedLocation =
      input.expectedSelectedLocationId !== undefined;
    const hasExpectedSelectedAssignment =
      input.expectedSelectedUserLocationId !== undefined;
    if (
      hasExpectedSourceLocation !== hasExpectedSourceAssignment ||
      hasExpectedSelectedLocation !== hasExpectedSelectedAssignment
    ) {
      return { status: "unavailable", reason: "owner-mismatch" };
    }

    const authority = inspectAuthorityStrict(input.resolutionId);
    if (authority.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "unavailable", reason: "unreadable" };
    }
    if (authority.status === "absent") {
      return { status: "unavailable", reason: "absent" };
    }
    if (
      authority.draft.kind !==
      "specialist-location-gateway-navigation-lineage"
    ) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "unavailable", reason: "unreadable" };
    }
    const lineage = authority.draft;
    if (
      (hasExpectedCustomer &&
        lineage.customerAccountId !== input.expectedCustomerAccountId) ||
      (hasExpectedSourceLocation &&
        (lineage.source.sourceLocationId !==
          input.expectedSourceLocationId ||
          lineage.source.sourceUserLocationId !==
            input.expectedSourceUserLocationId)) ||
      (hasExpectedSelectedLocation &&
        (lineage.selection.selectedLocationId !==
          input.expectedSelectedLocationId ||
          lineage.selection.selectedUserLocationId !==
            input.expectedSelectedUserLocationId))
    ) {
      return { status: "unavailable", reason: "owner-mismatch" };
    }

    const intentRead =
      readDurableSpecialistLocationGatewayIntentForLineageStrict(lineage);
    if (intentRead.status !== "ready") {
      return { status: "unavailable", reason: "unreadable" };
    }
    return { status: "ready", lineage, intent: intentRead.intent };
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unavailable", reason: "unreadable" };
  }
}

export function clearSpecialistLocationGatewayAuthorities() {
  try {
    return authorityStore.clearDrafts();
  } catch {
    return false;
  }
}
